import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase, logActivity } from '../lib/supabase';
import { useConfig } from '../contexts/ConfigContext';
import { useAuth } from '../contexts/AuthContext';
import AddendumsModal from '../components/AddendumsModal';
import ServiceOrderPreviewModal from '../components/ServiceOrderPreviewModal';
import EngagementPanel from '../components/EngagementPanel';
// v3.5.99.7.13: ETF Calculator (per-quote)
import ETFCalculatorModal from '../components/ETFCalculatorModal';
import { loadAddendumTemplates, validateAddendums } from '../lib/addendums';
// v3.5.67: per-user quote-type permissions
import { userCanCreateQuoteType } from '../lib/quotePermissions';
import {
  calcLocationMRR, getMultiSiteDiscount, applyMultiSiteDiscountWithFloor, getMultiSiteGmFloor, createLocation,
  fmt$, fmt$0, fmtPct, gmColor, gmBg,
  getBurdenedRate, getSeniorBurdenedRate, getItsoBillableRate, getItsoUpliftRate, getItsoLabel,
  getContractTermOptions
} from '../lib/pricing';
// v3.5.92.1: unified totals utility — Multi-Site migration (Phase 1
// item 2, sub-deploy 2 of 4). See CLAUDE.md §10b.
import { buildTotals } from '../lib/quoteTotals';
import { saveQuoteVersion } from '../lib/quoteVersions';
// v3.5.94.0: writeQuoteUrlToDeal added — Phase 1 item 3, Multi-Site parity
// gap #1 (HubSpot quote URL write-back). Other production quote types
// (IT/Voice/Bundle) auto-populate the configured HubSpot deal field with
// the quote URL on save; Multi-Site has been missing this since the page
// was introduced. Reps working a Multi-Site deal had to manually copy/
// paste the URL from the browser address bar.
import { writeQuoteUrlToDeal, searchDeals, getDealFull, updateDealDescription, captureHubspotSnapshot, pushHubspotGapFill, pushHubspotOverwrite } from '../lib/hubspot';
import { getOrAnalyzeMarket } from '../lib/marketRates';
import QuoteNotes from '../components/QuoteNotes';
import QuoteHistory from '../components/QuoteHistory';
import { ReviewBanner } from '../components/SendForReview';
import QuoteSaveBar from '../components/QuoteSaveBar';
import RepSelector from '../components/RepSelector';
import ThreatProfileInfo from '../components/ThreatProfileInfo';
import DomainsInfo from '../components/DomainsInfo';
import FlexTimeMeter   from '../components/FlexTimeMeter';
import FlexTimeSelector from '../components/FlexTimeSelector';
import { calcFlexBlock } from '../lib/flexTime';
import { DocumentsPanel } from '../components/RateSheetModal';
import OnboardingIncentive from '../components/OnboardingIncentive';
import ManualDiscounts from '../components/ManualDiscounts';
import OneTimeDiscounts from '../components/OneTimeDiscounts';
import AgreementEffectiveDate from '../components/AgreementEffectiveDate';
import { applyManualDiscounts } from '../lib/manualDiscounts';
import { applyOneTimeDiscounts } from '../lib/oneTimeDiscounts';
// v3.8.7 — Unified-UX workspace shell (flag ferrumiq_v3_multisite, default off).
import { WorkspaceShell, QuoteHeader, LiveTotalCard, RailCard, SubCard, Button as V3Button } from '../components/v3';
import { isQuoteLocked, QUOTE_LOCK_REASON } from '../lib/quoteLock';
import LockedQuoteBanner from '../components/LockedQuoteBanner';
import QuoteDocuments from '../components/QuoteDocuments';
import { v3 } from '../lib/styleTokens';
import HubSpotConnect from '../components/HubSpotConnect';
import SPTConnect    from '../components/SPTConnect';
import { loadPackageIncludes } from '../lib/packageIncludes';
import { loadPackageCostComponents } from '../lib/packageCostComponents';
import PackageIncludes from '../components/PackageIncludes';
import GMBadge from '../components/GMBadge';
// v3.5.86.2: shared AssumptionsModal mounted with quoteType="multi_site".
// Foundation refactor in v3.5.86.1 made the modal quote-type-aware.
import AssumptionsModal from '../components/AssumptionsModal';

const LOCATION_TYPES = { standard: 'Standard', restricted: 'Restricted' };
const TYPE_COLOR     = { standard: '#2563eb', restricted: '#d97706' };
const TYPE_BG        = { standard: '#eff6ff', restricted: '#fffbeb' };
const TYPE_DESC      = {
  standard:   'Full managed location — users, devices, servers, $location_fee/mo site fee, network layer',
  restricted: 'Co-work / shared space / 3rd-party IT — users and devices only, no network layer, no site fee',
};

export default function MultiSiteQuotePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const loc = useLocation();
  const { packages, products, marketTiers, settings, productsByCategory, exclusiveGroups, loading: configLoading } = useConfig();
  const { profile } = useAuth();
  // v3.5.67: quote-type permission gate (see QuotePage for full rationale).
  const _isNewQuote = !id;
  const _hasPermission = userCanCreateQuoteType(profile, 'multisite');
  const isReadOnly = !_isNewQuote && profile != null && !_hasPermission;
  useEffect(() => {
    if (_isNewQuote && profile != null && !_hasPermission) {
      navigate('/quotes', { replace: true, state: { permissionDenied: 'multisite' } });
    }
  }, [profile, _isNewQuote, _hasPermission, navigate]);

  // ── Shared proposal fields ────────────────────────────────────────────────
  const [proposalName,     setProposalName]     = useState('');
  const [recipientBiz,     setRecipientBiz]     = useState('');
  const [recipientContact, setRecipientContact] = useState('');
  const [recipientEmail,   setRecipientEmail]   = useState('');
  // v3.5.99.0: per-quote override for proposal-approved notification CC list.
  const [notificationCc,   setNotificationCc]   = useState('');
  // v3.5.99.4: per-quote addendums — see QuotePage canonical block for rationale.
  const [addendums,         setAddendums]         = useState([]);
  const [addendumTemplates, setAddendumTemplates] = useState([]);
  const [addendumsModalOpen, setAddendumsModalOpen] = useState(false);
  // v3.5.99.5: Service Order preview modal — opens the full SO PDF inline so
  // reps can sanity-check the document before sending to SignWell. The modal
  // calls /.netlify/functions/buildSignatureDocument directly (same backend
  // path the actual send uses), so what's previewed is exactly what gets sent.
  const [soPreviewModalOpen, setSoPreviewModalOpen] = useState(false);
  useEffect(() => { loadAddendumTemplates().then(setAddendumTemplates); }, []);
  const [recipientAddress, setRecipientAddress] = useState('');
  // v3.5.99.7.3: client phone/mobile pulled from HubSpot in connectDeal.
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientMobilePhone, setRecipientMobilePhone] = useState('');
  // v3.5.99.7.4: HubSpot write-back state. contactId/companyId route
  // updateContact/updateCompany calls. snapshot captures HubSpot's original
  // values at connectDeal time so gap-fill knows which fields to push.
  const [hubContactId, setHubContactId] = useState('');
  const [hubCompanyId, setHubCompanyId] = useState('');
  const [hubspotSnapshot, setHubspotSnapshot] = useState(null);
  // v3.5.99.7.13: ETF Calculator modal visibility
  const [showEtfModal, setShowEtfModal] = useState(false);
  // v3.5.99.7.6: manual full-overwrite push state. Distinct from the
  // existing hubMsg/hubSyncing state (which is QuotePage-only and used
  // by the older syncToDeal button). hubPushing/hubPushMsg track the
  // new pushAllToHubspot flow.
  const [hubPushing, setHubPushing] = useState(false);
  const [hubPushMsg, setHubPushMsg] = useState('');

  // v3.5.99.7.6: manual full-overwrite push to HubSpot. Unlike the
  // automatic gap-fill (which only pushes fields HubSpot was originally
  // missing), this pushes ALL current Ferrum values, overwriting whatever
  // is in HubSpot. Use when rep wants to force HubSpot to match Ferrum's
  // current state — typically after correcting a phone number or address
  // in Ferrum that HubSpot has stale.
  async function pushAllToHubspot() {
    if (!hubContactId && !hubCompanyId) {
      setHubPushMsg('No HubSpot deal linked — connect a deal first.');
      return;
    }
    setHubPushing(true); setHubPushMsg('');
    try {
      const r = await pushHubspotOverwrite({
        contactId: hubContactId, companyId: hubCompanyId,
        recipientContact, recipientEmail, recipientPhone, recipientMobilePhone, recipientAddress,
      });
      if (r.updatedSnapshot) setHubspotSnapshot(r.updatedSnapshot);
      const total = r.contactFields.length + r.companyFields.length;
      if (total > 0) {
        setHubPushMsg(`✓ Pushed ${total} field${total > 1 ? 's' : ''} (${r.contactFields.length} contact, ${r.companyFields.length} company)`);
      } else {
        setHubPushMsg('Nothing to push — all recipient fields are empty.');
      }
      if (r.errors?.length) {
        console.warn('HubSpot push errors:', r.errors);
        setHubPushMsg(prev => prev + ' · ⚠ some errors (see console)');
      }
    } catch (e) {
      setHubPushMsg('✗ ' + (e.message || 'Push failed'));
    }
    setHubPushing(false);
  }
  const [dealDescription,  setDealDescription]  = useState('');
  const [quoteStatus,      setQuoteStatus]      = useState('draft');
  const [saving,           setSaving]           = useState(false);
  const [saveMsg,          setSaveMsg]          = useState('');
  const [existingQuote,    setExistingQuote]    = useState(null);

  // ── Package / term / compliance ───────────────────────────────────────────
  const [selectedPkg,  setSelectedPkg]  = useState(null);
  const [contractTerm, setContractTerm] = useState(24);
  const [compliance,   setCompliance]   = useState('none');
  // v3.5.58: default Industry Risk = 'none' (no ITSO uplift on fresh quote)
  const [industryRisk, setIndustryRisk] = useState('none');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [manualQuantities, setManualQuantities] = useState({});
  const [flexHours,    setFlexHours]    = useState(null);

  // ── Package Includes (v3.5.31) ────────────────────────────────────────────
  // Loaded when selectedPkg changes. Same semantics as on QuotePage — but
  // applied per-location (each location's COGS picks up the includes' cost
  // contribution scaled by that location's user/workstation/server counts).
  // excludedIncludeIds applies once for the whole quote (not per-location).
  // includeSwaps (v3.5.33): same whole-quote scope. Substitutes price as
  // paid add-ons at full sell — applied per-location like add-ons do today.
  const [packageIncludes,    setPackageIncludes]    = useState([]);
  const [includesLoading,    setIncludesLoading]    = useState(false);
  const [excludedIncludeIds, setExcludedIncludeIds] = useState([]);
  const [includeSwaps,       setIncludeSwaps]       = useState({});

  // v3.5.41: cost components loaded per package (whole-quote scope, like includes).
  // v3.5.57: engine reads these unconditionally; no toggle to gate them.
  const [costComponents, setCostComponents] = useState([]);

  // ── Locations ─────────────────────────────────────────────────────────────
  const [locations,         setLocations]         = useState([createLocation({ name: 'Location 1' })]);
  const [locationAnalyses,  setLocationAnalyses]  = useState({});
  const [showLocModal,      setShowLocModal]       = useState(false);
  const [editingLoc,        setEditingLoc]         = useState(null);
  const [locLoading,        setLocLoading]         = useState({});

  // v3.5.86.2: Customer Story & Assumptions modal state. Mirrors the
  // QuotePage v3.5.81.6 pattern, scoped down for Multi-Site:
  //   - showAssumptions toggles the modal
  //   - assumptionsStory mirrors the saved story text for at-a-glance status
  //   - assumptionsSavedAt is the savedAt timestamp from the last save
  //   - assumptionsComplete drives the button color/icon (✓ green vs ⚠ amber)
  // Multi-Site doesn't yet have a Send Proposal flow (per CLAUDE.md punch
  // list), so the modal is currently a CAPTURE-ONLY surface — no required
  // gate. When MS signature flow lands, the gate gets added then.
  const [showAssumptions,    setShowAssumptions]    = useState(false);
  const [assumptionsStory,   setAssumptionsStory]   = useState('');
  const [assumptionsSavedAt, setAssumptionsSavedAt] = useState(null);
  const assumptionsComplete = !!(assumptionsStory && assumptionsStory.trim().length > 0);

  // ── HubSpot ───────────────────────────────────────────────────────────────
  const [hubDealId,   setHubDealId]   = useState('');
  const [hubDealUrl,  setHubDealUrl]  = useState('');
  const [hubDealName, setHubDealName] = useState('');

  // ── Rep ───────────────────────────────────────────────────────────────────
  const [obIncentive,      setObIncentive]      = useState(null);
  // v3.5.99.7.23: manual discount lines [{ type:'pct'|'fixed', value, note }]
  const [manualDiscounts,  setManualDiscounts]  = useState([]);
  const [oneTimeDiscounts, setOneTimeDiscounts] = useState([]);
  const [msFocusedSection, setMsFocusedSection] = useState(null);  // v3.8.7 workspace
  // v3.5.99.7.24: optional back-dated agreement effective date (YYYY-MM-DD)
  const [effectiveDate,    setEffectiveDate]    = useState('');
  const [pricingSnapshot, setPricingSnapshot] = useState(null);
  const [priceLockDate,   setPriceLockDate]   = useState(null);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [repId,       setRepId]       = useState(null);
  const [repProfile,  setRepProfile]  = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);

  // ── UI ────────────────────────────────────────────────────────────────────
  const [expandedLoc, setExpandedLoc] = useState(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [sptProposalId,    setSptProposalId]    = useState(null);

  // ── Load team members ─────────────────────────────────────────────────────
  useEffect(() => {
    supabase.from('profiles').select('id, full_name, email, commission_rate').order('full_name')
      .then(({ data }) => setTeamMembers(data || []));
  }, []);

  useEffect(() => {
    if (!repId && profile?.id && !id) setRepId(profile.id);
  }, [profile, id]);

  useEffect(() => {
    if (!repId || !teamMembers.length) return;
    const rep = teamMembers.find(m => m.id === repId);
    if (rep) setRepProfile(rep);
  }, [repId, teamMembers]);

  // ── Load existing quote ───────────────────────────────────────────────────
  useEffect(() => {
    if (!id || id === 'new' || configLoading) return;
    supabase.from('quotes').select('*').eq('id', id).single().then(({ data }) => {
      if (!data) return;
      setExistingQuote(data);
      setProposalName(data.inputs?.proposalName || '');
      setRecipientBiz(data.client_name || '');
      setRecipientContact(data.inputs?.recipientContact || '');
      setRecipientEmail(data.inputs?.recipientEmail || '');
      setNotificationCc(data.inputs?.notificationCc || '');
      setAddendums(Array.isArray(data.inputs?.addendums) ? data.inputs.addendums : []);
      setManualDiscounts(Array.isArray(data.inputs?.manualDiscounts) ? data.inputs.manualDiscounts : []);
      setOneTimeDiscounts(Array.isArray(data.inputs?.oneTimeDiscounts) ? data.inputs.oneTimeDiscounts : []);
      setEffectiveDate(data.inputs?.effectiveDate || '');
      setRecipientAddress(data.inputs?.recipientAddress || '');
      setRecipientPhone(data.inputs?.recipientPhone || '');
      setRecipientMobilePhone(data.inputs?.recipientMobilePhone || '');
      setHubContactId(data.inputs?.hubspotContactId || '');
      setHubCompanyId(data.inputs?.hubspotCompanyId || '');
      setHubspotSnapshot(data.inputs?.hubspotSnapshot || null);
      setDealDescription(data.notes || '');
      setQuoteStatus(data.status || 'draft');
      setHubDealId(data.hubspot_deal_id || '');
      setHubDealUrl(data.hubspot_deal_url || '');
      setHubDealName(data.inputs?.hubspotDealName || '');
      setContractTerm(data.inputs?.contractTerm || 24);
      setCompliance(data.inputs?.compliance || 'none');
      setIndustryRisk(data.inputs?.industryRisk || 'low');
      // v3.5.58: auto-strip retired product IDs (legacy vCIO Strategic Advisory).
      const rawSelected = Array.isArray(data.inputs?.selectedProducts) ? data.inputs.selectedProducts : [];
      const validIds = products.length > 0 ? new Set(products.filter(p => p.active).map(p => p.id)) : null;
      setSelectedProducts(validIds ? rawSelected.filter(pid => validIds.has(pid)) : rawSelected);
      setManualQuantities(data.inputs?.manualQuantities || {});
      // v3.5.31: restore rep's swappable-include exclusions
      if (Array.isArray(data.inputs?.excludedIncludeIds)) {
        setExcludedIncludeIds(data.inputs.excludedIncludeIds);
      }
      // v3.5.33: restore swap mappings
      if (data.inputs?.includeSwaps && typeof data.inputs.includeSwaps === 'object') {
        setIncludeSwaps(data.inputs.includeSwaps);
      }
      if (data.rep_id) setRepId(data.rep_id);
      if (data.pricing_snapshot) { setPricingSnapshot(data.pricing_snapshot); setPriceLockDate(data.price_locked_at); }
      if (data.spt_proposal_id) setSptProposalId(data.spt_proposal_id);
      if (data.inputs?.flexHours) setFlexHours(data.inputs.flexHours);
      if (data.inputs?.locations?.length) setLocations(data.inputs.locations);
      // v3.5.86.1.3: prefix-strip workaround was here to handle the
      // 'Multi-Site — Business Plus' shape that stored data used before
      // v3.5.91.2. Catalog stores bare package names ("Business Plus",
      // "Enterprise"); the original direct find always missed because of
      // the prefix.
      //
      // v3.5.91.2: prefix retired from stored data — data.package_name
      // is now just 'Business Plus' / 'Enterprise' / etc. for Multi-Site
      // quotes (matches Managed IT exactly; differentiation lives in
      // quote_type). Direct catalog lookup now works without any
      // string-massaging. The if(found) defensive guard is KEPT — falls
      // through to the default-package effect when the lookup fails
      // (e.g. catalog has been edited and a stored package_name no
      // longer matches any current package, or the rare case of a row
      // saved with selectedPkg undefined showing 'IT' as a fallback).
      if (data.package_name && packages.length) {
        const found = packages.find(p => p.name === data.package_name);
        if (found) setSelectedPkg(found);
      }
      // v3.5.44: restore onboarding incentive (column added in same release)
      if (data.onboarding_incentive?.mode) setObIncentive(data.onboarding_incentive);
      // v3.5.86.2: restore Customer Story for the at-a-glance status indicator.
      // Drives the ✓/⚠ on the Customer Story button. Same shape QuotePage uses.
      if (data.inputs?.assumptions) {
        setAssumptionsStory(data.inputs.assumptions.story || '');
        setAssumptionsSavedAt(data.inputs.assumptions.savedAt || null);
      }
    });
  }, [id, configLoading, packages]);

  // ── Package default ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedPkg && packages.length) setSelectedPkg(packages[1] || packages[0]);
  }, [packages]);

  // ── Package includes (v3.5.31) — loaded when selectedPkg changes ──────────
  useEffect(() => {
    if (!selectedPkg?.id) { setPackageIncludes([]); return; }
    setIncludesLoading(true);
    loadPackageIncludes(selectedPkg.id).then(rows => {
      setPackageIncludes(rows);
      setIncludesLoading(false);
      // v3.5.33: prune stale exclusions/swaps that don't match any current include
      const validIds = new Set(rows.map(r => r.id));
      setExcludedIncludeIds(prev => prev.filter(id => validIds.has(id)));
      setIncludeSwaps(prev => {
        const next = {};
        for (const [includeId, subId] of Object.entries(prev || {})) {
          if (validIds.has(includeId)) next[includeId] = subId;
        }
        return next;
      });
    });
  }, [selectedPkg?.id]);

  // v3.5.41: load cost components when selectedPkg changes
  useEffect(() => {
    if (!selectedPkg?.id) { setCostComponents([]); return; }
    loadPackageCostComponents(selectedPkg.id).then(rows => setCostComponents(rows));
  }, [selectedPkg?.id]);

  // ── Fetch market analysis per location when zip changes ───────────────────
  const fetchAnalysis = useCallback(async (locId, zip, readOnly = false) => {
    if (!zip || zip.length < 5) return;
    setLocLoading(prev => ({ ...prev, [locId]: true }));
    try {
      const result = await getOrAnalyzeMarket(zip, false, undefined, undefined, readOnly);
      if (result?.analysis) setLocationAnalyses(prev => ({ ...prev, [locId]: result.analysis }));
    } catch (e) { console.warn('Market analysis failed:', e.message); }
    setLocLoading(prev => ({ ...prev, [locId]: false }));
  }, []);

  // Re-fetch when locations change
  // readOnly=true for saved quotes — reads DB only, never triggers AI refresh
  useEffect(() => {
    const isExisting = !!existingQuote?.id;
    locations.forEach(l => {
      if (l.zip && l.zip.length >= 5 && !locationAnalyses[l.id]) {
        fetchAnalysis(l.id, l.zip, isExisting);
      }
    });
  }, [locations.map(l => l.id + l.zip).join(',')]);

  // ── Pricing calcs ─────────────────────────────────────────────────────────
  const locationResults = useMemo(() => {
    if (!selectedPkg || configLoading) return [];
    return locations.map(loc => {
      const analysis = locationAnalyses[loc.id];
      const mktMult  = analysis?.pricing_multiplier ?? 1;
      const result   = calcLocationMRR({ location: loc, pkg: selectedPkg, marketMultiplier: mktMult, settings, packageIncludes, excludedIncludes: excludedIncludeIds, includeSwaps, products, costComponents });
      return { loc, result, analysis };
    });
  }, [locations, locationAnalyses, selectedPkg, settings, configLoading, packageIncludes, excludedIncludeIds, includeSwaps, products, costComponents]);

  const totalLocationMRR = locationResults.reduce((s, r) => s + (r.result?.mrr || 0), 0);

  // Add-on products (applied once, same logic as IT quote)
  const { addonRevenue, protectedAddonRevenue, addonCost, addonLineItems } = useMemo(() => {
    let discountable = 0, protected_ = 0, cost = 0;
    const lines = [];
    for (const p of products.filter(pr => selectedProducts.includes(pr.id))) {
      const qty     = (p.qty_driver === 'user')
        ? locations.reduce((s, l) => s + (parseInt(l.users) || 0), 0)
        : (p.qty_driver === 'workstation')
          ? locations.reduce((s, l) => s + (parseInt(l.workstations) || 0), 0)
          // v3.5.96.0: 'domain' driver — sums per-location domains across the
          // quote so products billing per-domain (DNS management, certificate
          // renewal, monitoring, etc.) charge the correct total. Slotted
          // alongside 'user' and 'workstation' since those are the only other
          // MultiSite-aware drivers; other drivers (server, location, etc.)
          // would need similar branches if MultiSite ever needs to bill
          // products per-server / per-location — out of scope for this deploy.
          : (p.qty_driver === 'domain')
            ? locations.reduce((s, l) => s + (parseInt(l.domains) || 0), 0)
          : (p.qty_driver === 'manual')
            ? parseInt(manualQuantities?.[p.id] || 0)
            : 1;
      const rev  = qty * p.sell_price;
      const cst  = qty * p.cost_price;
      if (p.no_discount) protected_ += rev; else discountable += rev;
      cost += cst;
      lines.push({ ...p, qty, revenue: rev, cost: cst });
    }
    return { addonRevenue: discountable + protected_, protectedAddonRevenue: protected_, addonCost: cost, addonLineItems: lines };
  }, [selectedProducts, products, locations, manualQuantities]);

  // v3.5.95.0 — restructured engine for the margin-guarded multi-site
  // discount. Phase 1 of Option B (Shaun, May 12 2026) per §10f #4. Pre-
  // deploy the discount block at line 319 came FIRST, then per-location
  // cost aggregations and the flex block came later (lines 363, 389-402)
  // — meaning the engine applied the configured discount rate before
  // any GM check was possible. The new solver requires fixedCost as an
  // input, which requires those aggregations to be done first. So we
  // topologically reorder: all r-independent quantities first, solver
  // call, then discount chain.
  //
  // All output identifiers keep their pre-deploy names and final values
  // (multiDiscRate, finalLaborMRR, itsoMRR, itsoCost, finalMRR, the four
  // location-result reductions, etc.) — downstream code at line ~370+
  // (commission, onboarding, effectiveMRR/Cost, persistence at line 515)
  // is untouched. Only the order of derivation changes.

  // ── r-independent #1: term discount rate ──
  const discKey      = `contract_disc_${contractTerm}`;
  const termDiscRate = parseFloat(settings[discKey]) || 0;

  // ── r-independent #2: per-location cost + revenue aggregations ──
  // v3.5.31: sum per-location includes cost for margin display
  const totalIncludesCost = locationResults.reduce((s, r) => s + (r.result?.includedItemsCost || 0), 0);
  // v3.5.33: sum per-location swap-substitute cost for margin display.
  // Note: swap REVENUE is already included in totalLocationMRR via location.mrr,
  // so we don't double-count revenue. Only cost needs to be aggregated separately.
  const totalSwapCost = locationResults.reduce((s, r) => s + (r.result?.swapCost || 0), 0);
  // v3.5.39: aggregate per-location tooling + labor costs.
  // These were previously NOT included in effectiveCost — a real bug that made
  // every MultiSite quote's GM appear materially higher than it actually was
  // (~25-40 percentage points off). Fixed there; identifiers preserved here.
  const totalToolingCost = locationResults.reduce((s, r) => s + (r.result?.toolingCost || 0), 0);
  const totalSvcCost     = locationResults.reduce((s, r) => s + (r.result?.svcCost     || 0), 0);
  const totalSvcHrs      = locationResults.reduce((s, r) => s + (r.result?.svcHrs      || 0), 0);

  // ── r-independent #3: flex block + labor cost ──
  // (Moved up from below the discount chain — flexBlockMRR feeds the GM
  // floor check and flexLaborCost feeds fixedCost.)
  const flexBlock      = (selectedPkg && flexHours && locationResults.length > 0)
    ? calcFlexBlock(flexHours, locationResults[0]?.analysis?.rates?.remote_support || 165, settings) : null;
  const flexBlockMRR   = flexBlock?.blockPrice || 0;
  // v3.5.58: settings-driven burden helper (default $80, was $125 fallback).
  const burdenedRate   = getBurdenedRate(settings);
  const flexLaborCost  = flexBlock ? flexBlock.hours * burdenedRate : 0;

  // ── r-independent #4: ITSO pre-discount components ──
  // ── ITSO Advisory line at quote level (v3.5.58, retuned v3.5.76) ──────────
  // Cross-location aggregation: uplift % applied to the totalLocationMRR (sum
  // of per-location market-adjusted bases). MultiSite-specific math:
  //   • Base = totalLocationMRR (gross, pre-multi-site, pre-term discount)
  //   • Both discounts apply to ITSO consistently with how labor MRR is
  //     treated, so reps see one coherent post-discount number.
  //   • Compliance/Complexity multipliers do NOT apply here — MultiSite
  //     historically didn't apply them to per-location MRR (calcLocationMRR
  //     doesn't read them); v3.5.58 preserves that behavior to avoid an
  //     unrelated stealth pricing change. The ITSO line is the only new
  //     multiplier-style mechanism MultiSite gets in this deploy.
  //
  // v3.5.76 Option B-prime (parity with v3.5.74 in the central pricing.js
  // engine): hours derived from ITSO billable rate (admin-editable, default
  // $200/hr), cost = hours × senior burdened rate ($110/hr default). The
  // ITSO line on MultiSite quotes now carries ~45% GM at default rates,
  // matching QuotePage and Bundle behavior. The previous Option B model
  // (cost = revenue, zero GM) never made sense as a default — see
  // pricing.js getItsoBillableRate() for the full architecture rationale.
  const itsoUpliftRate = getItsoUpliftRate(settings, industryRisk);
  const itsoBaseMRR    = totalLocationMRR;
  const itsoGross      = Math.round(itsoBaseMRR * itsoUpliftRate * 100) / 100;
  const itsoLabel      = getItsoLabel(settings, industryRisk);
  const seniorBurden   = getSeniorBurdenedRate(settings);
  const itsoBillable   = getItsoBillableRate(settings);

  // ── r-independent #5: fixedCost (everything in effectiveCost EXCEPT itsoCost) ──
  // itsoCost is r-dependent because itsoMRR = I × (1−r)(1−t) and
  // itsoCost = itsoMRR × (seniorBurden / itsoBillable). The solver handles
  // that scalar internally. Everything else here is fixed.
  const fixedCost = totalToolingCost + totalSvcCost + addonCost + flexLaborCost + totalIncludesCost + totalSwapCost;

  // ── v3.5.95.0 SOLVER: GM-floor-aware multi-site discount rate ──
  // Pre-deploy: multiDiscRate = getMultiSiteDiscount(locations.length, settings)
  //   — unconditional tier-lookup, applied regardless of margin impact.
  // Post-deploy: getMultiSiteDiscount gives the CONFIGURED rate; the solver
  //   in pricing.js returns the maximum applicable rate ≤ configured that
  //   keeps GM ≥ floor. See pricing.js applyMultiSiteDiscountWithFloor for
  //   the algebraic derivation.
  const multiDiscRateConfigured = getMultiSiteDiscount(locations.length, settings);
  const gmFloor                 = getMultiSiteGmFloor(settings);
  const _discResult = applyMultiSiteDiscountWithFloor({
    rateConfigured: multiDiscRateConfigured,
    totalLocationMRR,
    itsoGross,
    termDiscRate,
    addonRevenue,
    flexBlockMRR,
    fixedCost,
    itsoBillable,
    seniorBurden,
    gmFloor,
  });
  const multiDiscRate       = _discResult.rateApplied;
  const multiDiscRateCapped = _discResult.capped;

  // ── Discount chain (math unchanged; only the source of multiDiscRate differs) ──
  const multiDiscAmount  = totalLocationMRR * multiDiscRate;
  const discountedLocMRR = totalLocationMRR - multiDiscAmount;
  const termDisc         = discountedLocMRR * termDiscRate;
  const finalLaborMRR    = discountedLocMRR - termDisc;

  // ── ITSO post-discount components ──
  const itsoAfterMulti = itsoGross * (1 - multiDiscRate);
  const itsoMRR        = Math.round(itsoAfterMulti * (1 - termDiscRate) * 100) / 100;
  const itsoHours      = itsoMRR > 0 && itsoBillable > 0 ? itsoMRR / itsoBillable : 0;
  const itsoCost       = Math.round(itsoHours * seniorBurden * 100) / 100;

  // v3.5.99.7.23: manual discounts apply to the composed contract MRR. The net
  // becomes finalMRR, so effectiveMRR / GM / TCV / commission-net and every
  // display site below reflect it. Save passes the aggregate engineResult +
  // manualDiscounts to buildTotals, which applies the discount once — the page
  // and the persisted totals stay in lockstep.
  const finalMRRPreDiscount = finalLaborMRR + addonRevenue + itsoMRR;
  const manualDiscountResolved = applyManualDiscounts(finalMRRPreDiscount, manualDiscounts);
  const finalMRR = manualDiscountResolved.finalMRR;

  // Commission — v3.5.58: ITSO MRR is part of the labor base for commission
  // (consistent with QuotePage where ITSO flows into discountedLabor).
  const commissionRate   = repProfile?.commission_rate ?? (parseFloat(settings.commission_rate) || 0);
  const commissionBase   = finalLaborMRR + itsoMRR;
  const commission       = commissionBase * commissionRate;
  const netAfterCommission = finalMRR - commission;

  // Onboarding
  const obMin    = parseFloat(settings.onboarding_min) || 500;
  const totalUsers = locations.reduce((s, l) => s + (parseInt(l.users) || 0), 0);
  const totalWS    = locations.reduce((s, l) => s + (parseInt(l.workstations) || 0), 0);
  const totalSrv   = locations.reduce((s, l) => s + (parseInt(l.servers) || 0), 0);
  // v3.5.96.0: totalDomains aggregated across locations. Persisted to
  // totals.multisite below so the proposal renderer's per-location
  // breakdown table can show per-location domains and the customer-
  // facing onboarding fee correctly reflects the per-domain contribution.
  const totalDomains = locations.reduce((s, l) => s + (parseInt(l.domains) || 0), 0);
  const obCalc     = totalUsers * (parseFloat(settings.onboarding_per_user) || 35)
                   + totalWS    * (parseFloat(settings.onboarding_per_ws)   || 20)
                   + totalSrv   * (parseFloat(settings.onboarding_per_server)|| 250)
                   + locations.length * (parseFloat(settings.onboarding_per_location) || 450)
                   // v3.5.96.0: per-domain onboarding contribution. Default $99
                   // if pricing_settings.onboarding_per_domain row absent (SQL
                   // migration ships separately).
                   + totalDomains * (parseFloat(settings.onboarding_per_domain) || 99);
  const onboarding = Math.max(obCalc, obMin);
  const contractValue = finalMRR * contractTerm + onboarding;

  // v3.7.8: one-time discount bases. Multi-Site has only the Onboarding bucket
  // (post-incentive base). resolved drives the live net + the Stripe prepay.
  const oneTimeBases = {
    onboarding: (obIncentive?.effectiveFee != null ? Number(obIncentive.effectiveFee) : onboarding),
    hardware: 0, activation: 0, porting: 0,
  };
  const oneTimeResolved = applyOneTimeDiscounts(oneTimeBases, oneTimeDiscounts);


  const effectiveMRR  = finalMRR + flexBlockMRR;
  // v3.5.58: itsoCost included so MultiSite GM math reflects the senior
  // staff time committed to the deal at the chosen Industry Risk tier.
  const effectiveCost = totalToolingCost + totalSvcCost + addonCost + flexLaborCost + totalIncludesCost + totalSwapCost + itsoCost;
  const gc = gmColor(effectiveMRR > 0 ? 1 - effectiveCost / effectiveMRR : 0);
  const gb = gmBg(effectiveMRR > 0 ? 1 - effectiveCost / effectiveMRR : 0);

  // ── Location modal helpers ────────────────────────────────────────────────
  function openAddLoc() {
    setEditingLoc(createLocation({ name: `Location ${locations.length + 1}` }));
    setShowLocModal(true);
  }
  function openEditLoc(loc) { setEditingLoc({ ...loc }); setShowLocModal(true); }
  function removeLoc(locId) { setLocations(prev => prev.filter(l => l.id !== locId)); }

  function saveLocModal() {
    if (!editingLoc.name.trim()) return;
    const isNew = !locations.find(l => l.id === editingLoc.id);
    if (isNew) {
      setLocations(prev => [...prev, editingLoc]);
    } else {
      setLocations(prev => prev.map(l => l.id === editingLoc.id ? editingLoc : l));
      // Invalidate cached analysis if zip changed
      const orig = locations.find(l => l.id === editingLoc.id);
      if (orig?.zip !== editingLoc.zip) {
        setLocationAnalyses(prev => { const n = { ...prev }; delete n[editingLoc.id]; return n; });
      }
    }
    // Trigger analysis fetch
    if (editingLoc.zip?.length >= 5) fetchAnalysis(editingLoc.id, editingLoc.zip);
    setShowLocModal(false);
    setEditingLoc(null);
  }

  // ── Product toggle ────────────────────────────────────────────────────────
  function toggleProduct(pid) {
    const p = products.find(pr => pr.id === pid);
    if (!p) return;
    setSelectedProducts(prev => {
      let sel = [...prev];
      if (sel.includes(pid)) return sel.filter(x => x !== pid);
      if (p.exclusive_group) {
        const grp = exclusiveGroups[p.exclusive_group] || [];
        sel = sel.filter(x => !grp.includes(x));
      }
      return [...sel, pid];
    });
  }

  // ── HubSpot ───────────────────────────────────────────────────────────────


  // ── Save ──────────────────────────────────────────────────────────────────
  async function saveQuote() {
    // v3.5.99.4: block save if any addendum has unfilled required placeholders.
    // See QuotePage canonical block for rationale.
    const addendumErrors = validateAddendums(addendums, addendumTemplates);
    if (addendumErrors.length > 0) {
      const summary = addendumErrors.slice(0, 3).map(e => `\u2022 ${e.template_name}: ${e.placeholder_label || e.reason}`).join('\n');
      const more = addendumErrors.length > 3 ? `\n\u2026and ${addendumErrors.length - 3} more.` : '';
      alert(`Can't save \u2014 ${addendumErrors.length} addendum issue${addendumErrors.length === 1 ? '' : 's'}:\n\n${summary}${more}\n\nOpen Special Terms to fix or remove the addendum.`);
      return;
    }
    // v3.5.67: read-only gate — see QuotePage saveQuote for rationale.
    if (isReadOnly) {
      setSaveMsg('You don\'t have permission to save Multi-Site IT quotes. This quote is view-only.');
      return;
    }
    // v3.9.9.2: post-signature lock — a signed agreement freezes terms; persist ONLY notes.
    if (isQuoteLocked(existingQuote)) {
      setSaving(true); setSaveMsg('');
      try {
        if (existingQuote?.id) {
          await supabase.from('quotes').update({ notes: dealDescription }).eq('id', existingQuote.id);
          if (hubDealId && dealDescription) { try { await updateDealDescription(hubDealId, dealDescription); } catch { /* non-blocking */ } }
        }
        setSaveMsg('🔒 Locked — only internal notes were saved.');
      } catch (e) { setSaveMsg('✗ ' + (e?.message || 'Save failed')); }
      setSaving(false);
      return;
    }
    if (!recipientBiz.trim()) { setSaveMsg('Enter a client name first.'); return; }
    if (locations.length === 0) { setSaveMsg('Add at least one location.'); return; }
    setSaving(true); setSaveMsg('');

    const allInputs = {
      // v3.9.9: spread existing inputs FIRST so signwellDocuments (sent SO / LOA /
      // IntlWaiver tracking) and any other non-form fields survive the save — a
      // save after a document was sent otherwise wiped the record (and the
      // webhook's auto-charge target). Matches FlexITQuotePage (v3.5.52).
      ...(existingQuote?.inputs || {}),
      proposalName, recipientContact, recipientEmail, recipientAddress, recipientPhone, recipientMobilePhone, hubspotContactId: hubContactId || null, hubspotCompanyId: hubCompanyId || null, hubspotSnapshot, notificationCc,
      hubspotDealName: hubDealName, contractTerm, compliance, industryRisk,
      selectedProducts, locations, flexHours: flexHours || null,
      manualQuantities,
      repId: repId || null, repName: repProfile?.full_name || repProfile?.email || null,
      // v3.5.31: persist rep's swappable-include exclusions
      excludedIncludeIds: excludedIncludeIds || [],
      // v3.5.33: persist swap mappings
      includeSwaps: includeSwaps || {},
      // v3.5.99.7.23: persist manual discount lines
      manualDiscounts: manualDiscounts || [],
      // v3.7.8: persist one-time (non-recurring) discount lines
      oneTimeDiscounts: oneTimeDiscounts || [],
      // v3.5.99.7.24: persist back-dated agreement effective date
      effectiveDate: effectiveDate || null,
    };
    // v3.5.92.1: totals construction migrated to the unified utility at
    // src/lib/quoteTotals.js. Page-side math (effectiveMRR / effectiveCost
    // / per-location aggregates) stays exactly as-is for runtime display;
    // we just pass the engine-equivalent components through to buildTotals
    // and use its output for persistence (both upsert payload AND
    // saveQuoteVersion call below — identity persistence per §6 #6).
    //
    // What this changes for Multi-Site saves:
    //   • New persisted fields: effectiveMRR (= finalMRR + flexBlockMRR),
    //     nrc (= onboarding on Multi-Site; no hardware/porting), the all-
    //     zero hardwareNRC/activationNRC/portingNRC parity fields, ITSO
    //     fields (itsoMRR / itsoLabel / itsoUpliftRate) that the page has
    //     been computing at lines 339–344 but never persisting, full
    //     incentive metadata (effectiveOnboarding / onboardingDiscountAmount
    //     / onboardingIncentiveMode / onboardingIncentivePct), and the
    //     multisite: {...} bucket capturing termDiscRate / finalLaborMRR /
    //     addonRevenue / totalLocationMRR which were entirely lost on
    //     pre-deploy saves.
    //   • Behavior change: persisted contractValue now uses effectiveMRR
    //     (with flex) and the post-incentive labor figure, matching what
    //     the customer actually pays. Pre-deploy contractValue =
    //     finalMRR × term + raw onboarding; post-deploy contractValue =
    //     (finalMRR + flexBlockMRR) × term + effectiveOnboarding. Same
    //     direction of change as the IT migration in v3.5.92.0.
    //   • impliedGM math is identical to the pre-deploy formula (verified
    //     by inspection: page used effectiveMRR > 0 ? 1 - effectiveCost /
    //     effectiveMRR : 0; utility computes the same with `null` at the
    //     edge instead of `0` — coerces back to 0 at all consumer sites).
    //
    // engineResult dict is the page's pre-flex revenue and cost
    // components — utility computes flex labor cost from flexBlock +
    // settings internally, same as the IT path, so both quote types
    // derive flex labor identically.
    const totals = buildTotals('multisite', {
      // Revenue components
      finalLaborMRR,
      addonRevenue,
      itsoMRR,
      itsoLabel,
      itsoUpliftRate,
      // NRC (labor only on Multi-Site)
      onboarding,
      // Discounts + meta
      multiDiscRate,
      // v3.5.95.0: Margin-guarded discount engine — persist the configured
      // rate (from the tier lookup) and the capped flag (true iff GM floor
      // reduced the applied rate below configured). multiDiscRate above
      // remains the APPLIED rate (backward compat for existing consumers
      // at proposal renderer, quoteDiff, QuoteHistory — they all want the
      // applied value).
      multiDiscRateConfigured,
      multiDiscRateCapped,
      termDiscRate,
      totalLocationMRR,
      locationCount: locations.length,
      // v3.5.96.0: totalDomains persisted so the proposal renderer's
      // per-location breakdown table can render a Domains column with
      // aggregate totals matching what the engine billed. Field added
      // to MULTI_SITE_TOTALS_FIELDS in quoteDiff.js so QuoteHistory
      // tracks revision-to-revision changes in domain count too.
      totalDomains,
      // Cost components (PRE-FLEX — utility adds flex labor uniformly).
      // The page's effectiveCost at line 398 includes flexLaborCost; we
      // pass the underlying components individually here so the utility
      // can re-add flex labor the same way the IT path does.
      toolingCost:  totalToolingCost,
      svcCost:      totalSvcCost,
      addonCost,
      includesCost: totalIncludesCost,
      swapCost:     totalSwapCost,
      itsoCost,
    }, {
      obIncentive,
      flexBlock,
      contractTerm,
      settings,
      manualDiscounts,
      oneTimeDiscounts,
    });
    const payload = {
      client_name: recipientBiz, status: quoteStatus, notes: dealDescription,
      // v3.5.86.1.2: REVERTED to the prefixed format. v3.5.86.1.1 attempted
      // to switch this to `selectedPkg?.name` directly, on the (incorrect)
      // assumption that the prefix was just display cruft. It turned out
      // the prefix is read elsewhere as a quote-type discriminator —
      // probably in QuotesPage.js routing or the App.js router. Stripping
      // it broke Multi-Site type detection (page rendered wrong, locations
      // stopped showing). The rollback SQL in this same deploy restores
      // the prefix on the rows that were stripped.
      //
      // The package-load-shows-null bug (Shaun's original report) is real
      // but needs a different fix: either a proper quote_type column (no
      // string discriminators), or the load logic strips the prefix before
      // looking up against the catalog. That work waits for QuotesPage.js
      // and App.js uploads so we can see the full type-detection picture.
      //
      // The load-side hardening from v3.5.86.1.1 (the `if (found)` guard
      // below at the load callsite) is KEPT — it's strictly defensive and
      // doesn't depend on the save format.
      package_name: selectedPkg?.name || 'IT',
      // v3.5.91.0: dual-write quote_type. Canonical key 'multisite' (NOT
      // 'multi_site') matches src/lib/quotePermissions.js QUOTE_TYPES +
      // profiles.allowed_quote_types + RLS. v3.5.91.2: this column is
      // now the SOLE type discriminator (package_name no longer carries
      // the 'Multi-Site — ' prefix).
      quote_type: 'multisite',
      inputs: allInputs, totals,
      line_items: addonLineItems,
      hubspot_deal_id: hubDealId || null, hubspot_deal_url: hubDealUrl || null,
      // v3.5.44: persist onboarding incentive
      onboarding_incentive: obIncentive?.mode ? {
        mode:           obIncentive.mode,
        effectiveFee:   obIncentive.effectiveFee,
        monthlyAdd:     obIncentive.monthlyAdd   || 0,
        splitMonths:    obIncentive.splitMonths  || 0,
        discountPct:    obIncentive.discountPct  || 0,
        discountAmount: obIncentive.discountAmount || 0,
        fullFee:        onboarding || null,
      } : null,
      rep_id: repId || profile?.id || null,
      ...(quoteStatus === 'approved' && !pricingSnapshot ? { pricing_snapshot: { lockedAt: new Date().toISOString() }, price_locked_at: new Date().toISOString(), price_locked_by: profile?.id } : {}),
      ...(pricingSnapshot ? { pricing_snapshot: pricingSnapshot, price_locked_at: priceLockDate } : {}),
      updated_by: profile?.id,
    };
    if (!existingQuote) payload.created_by = profile?.id;

    const { data, error } = existingQuote
      ? await supabase.from('quotes').update(payload).eq('id', existingQuote.id).select().single()
      : await supabase.from('quotes').insert(payload).select().single();

    if (error) { setSaveMsg('Error: ' + error.message); setSaving(false); return; }

    if (hubDealId && dealDescription) {
      try { await updateDealDescription(hubDealId, dealDescription); } catch {}
    }

    await logActivity({ action: existingQuote ? 'UPDATE' : 'CREATE', entityType: 'quote',
      entityId: data.id, entityName: recipientBiz,
      changes: { type: 'multi-site', locations: locations.length, mrr: finalMRR } });

    // v3.5.94.0: HubSpot quote URL write-back. Mirrors QuotePage:690-699 —
    // when the deal is linked AND pricing_settings.hubspot_quote_url_field is
    // configured, push the public quote URL into the configured HubSpot deal
    // field so reps don't have to manually copy/paste from the browser bar.
    // Multi-Site parity fix per Phase 1 item 3 gap #1.
    if (hubDealId && data?.id) {
      try {
        const { data: fieldData } = await supabase.from('pricing_settings').select('value').eq('key','hubspot_quote_url_field').single();
        const fieldKey = fieldData?.value;
        if (fieldKey) {
          const quoteUrl = `${window.location.origin}/multisite/${data.id}`;
          await writeQuoteUrlToDeal(hubDealId, quoteUrl, fieldKey);
        }
      } catch (e) { console.warn('HubSpot quote URL write failed:', e.message); }
    }

    // v3.5.94.0: enriched quoteData metadata for parity with QuotePage. Pre-
    // deploy this site passed only { client_name, status }, so the diff engine's
    // QUOTE_FIELDS pass (which reads market_tier / package_name / client_zip)
    // never saw Multi-Site-specific top-level changes in QuoteHistory. With
    // these fields included, market-tier shifts and package renames now diff
    // correctly. quote_type added for the diff engine's new Multi-Site
    // detection path (see v3.5.94.0 quoteDiff changes).
    await saveQuoteVersion({ quoteId: data.id, quoteData: {
        client_name:  recipientBiz,
        client_zip:   locations[0]?.zip || null,
        market_tier:  locationAnalyses[locations[0]?.id]?.market_tier || null,
        package_name: selectedPkg?.name || null,
        quote_type:   'multisite',
        status:       quoteStatus,
      },
      inputs: allInputs, totals, lineItems: addonLineItems, profile });

    setSaveMsg(`Saved as ${data.quote_number}`);
    // v3.5.99.7.4: gap-fill push to HubSpot. Fire-and-forget after save
    // success — pushes only fields where HubSpot was empty when connectDeal
    // pulled (per hubspotSnapshot). Errors logged but don't block UI.
    // The manual full-overwrite push button comes in v3.5.99.7.5.
    if ((hubContactId || hubCompanyId) && hubspotSnapshot) {
      pushHubspotGapFill({
        contactId: hubContactId, companyId: hubCompanyId, snapshot: hubspotSnapshot,
        recipientContact, recipientEmail, recipientPhone, recipientMobilePhone, recipientAddress,
      }).then(r => {
        if (r.updatedSnapshot) setHubspotSnapshot(r.updatedSnapshot);
        if (r.errors?.length) console.warn('HubSpot gap-fill errors:', r.errors);
      }).catch(e => console.warn('HubSpot gap-fill failed:', e));
    }
    setSaving(false);
    setExistingQuote(data);
    if (!id || id === 'new') navigate(`/multisite/${data.id}`, { replace: true });
  }

  // ── Convert to single location ────────────────────────────────────────────
  function convertToSingle() {
    const first = locations[0];
    navigate('/quotes/new', { state: { fromBundle: {
      type: 'it',
      clientName: recipientBiz, clientZip: first?.zip || '',
      marketTier: locationAnalyses[first?.id]?.market_tier,
      packageName: selectedPkg?.name,
      proposalName, recipientContact, recipientEmail, recipientAddress, recipientPhone, recipientMobilePhone, hubspotContactId: hubContactId || null, hubspotCompanyId: hubCompanyId || null, hubspotSnapshot, notificationCc,
      notes: dealDescription, hubDealId, hubDealUrl, hubDealName,
      inputs: {
        users: first?.users || 0, workstations: first?.workstations || 0,
        servers: first?.servers || 0, endpoints: first?.endpoints || 0,
        mobileDevices: first?.mobileDevices || 0,
        contractTerm, compliance, industryRisk, selectedProducts,
        manualQuantities,
        locations: 1,
      },
      sourceQuoteId: existingQuote?.id, sourceQuoteNum: existingQuote?.quote_number,
    }}});
  }

  // ── SPT export ────────────────────────────────────────────────────────────
  function exportSPT() {
    if (!existingQuote) return;
    const json = {
      quote_number: existingQuote.quote_number,
      quote_type: 'multi-site-managed-it',
      proposal_name: proposalName,
      recipient: { business_name: recipientBiz, contact_name: recipientContact, email: recipientEmail, address: recipientAddress },
      date: new Date().toISOString().split('T')[0],
      package: selectedPkg?.name,
      contract_term_months: contractTerm,
      locations: locations.map((l, i) => ({
        number: i + 1,
        name: l.name,
        address: [l.address, l.city, l.state, l.zip].filter(Boolean).join(', '),
        type: l.type,
        users: l.users, workstations: l.workstations, servers: l.servers,
        market: locationAnalyses[l.id] ? `${locationAnalyses[l.id].city}, ${locationAnalyses[l.id].state}` : l.zip,
        market_tier: locationAnalyses[l.id]?.market_tier || 'standard',
        mrr: locationResults.find(r => r.loc.id === l.id)?.result?.mrr || 0,
        onsite_rate: locationAnalyses[l.id]?.rates?.remote_support || 165,
        dispatch_block: locationAnalyses[l.id]?.rates?.onsite_block_2hr || 330,
      })),
      location_subtotal_mrr: totalLocationMRR,
      multi_location_discount_rate: multiDiscRate,
      multi_location_discount_amount: multiDiscAmount,
      contract_term_discount_rate: termDiscRate,
      add_ons: addonLineItems,
      monthly_mrr: finalMRR,
      onboarding_fee: onboarding,
      contract_value: contractValue,
      hubspot_deal_id: hubDealId,
      // ASSUMPTIONS — legally binding location list
      covered_locations_assumption: `This agreement covers the following ${locations.length} location${locations.length > 1 ? 's' : ''} only. Any additional locations not listed below are NOT covered under this agreement and will require a separate addendum:\n\n` +
        locations.map((l, i) => `${i+1}. ${l.name} — ${[l.address, l.city, l.state, l.zip].filter(Boolean).join(', ')} (${LOCATION_TYPES[l.type]})`).join('\n'),
    };
    const a = document.createElement('a');
    a.href = 'data:application/json,' + encodeURIComponent(JSON.stringify(json, null, 2));
    a.download = `${existingQuote.quote_number}_spt.json`;
    a.click();
  }

  // v3.5.59: 5-value compliance dropdown — direct framework → tag mapping.
  // Legacy 'moderate'/'high' kept for any test quotes that pre-date migration.
  const complianceKey = (({ hipaa:['hipaa'], soc2:['soc2'], pci:['pci'], cmmc:['cmmc'], moderate:['hipaa','soc2'], high:['pci','cmmc'] })[compliance] || []);

  if (configLoading) return <div style={{ padding:24, color:'#6b7280', fontSize:12 }}>Loading...</div>;

  const ccSurcharge = parseFloat(settings?.payment_cc_surcharge) || 0.02;
  const checkFee    = parseFloat(settings?.payment_check_fee)    || 10;

  // ─── v3.8.7 Unified-UX workspace (flag ferrumiq_v3_multisite, DEFAULT OFF) ───
  // Same pattern as the other builders: legacy two-pane return below is
  // untouched; v3 reuses every handler + child component + verbatim <Sec>
  // blocks + the location/convert modals. GM-floor-capped discount is DERIVED
  // (no slider — audit "build only what exists"); NO bulk-edit (F-10 DROP);
  // "Convert to single location" kept. buildTotals path unchanged.
  const V3_MS = (() => {
    try {
      if (typeof window !== 'undefined') {
        const q = new URLSearchParams(window.location.search);
        if (q.get('v3') === '1') return true;
        if (q.get('v3') === '0') return false;
        const stored = window.localStorage.getItem('ferrumiq_v3_multisite');
        if (stored === '1') return true;
        if (stored === '0') return false;                     // persistent opt-out
        return true;   // v3.8.8 — Multi-Site workspace is now the DEFAULT (Shaun 2026-08-04).
      }                //          Classic layout reachable via ?v3=0 / stored '0'.
    } catch (e) { /* disabled storage */ }
    return true;
  })();

  if (V3_MS) {
    const activeSection = msFocusedSection || 'locations';
    const statusVariant = ({ won: 'green', approved: 'green', sent: 'blue', lost: 'red', expired: 'red', in_review: 'amber', draft: 'amber' })[quoteStatus] || 'gray';
    const t2 = (t) => (<h2 style={{ fontSize: v3.font.size.section, fontWeight: 600, letterSpacing: '-0.5px', margin: 0 }}>{t}</h2>);
    const s2 = (t) => (<p style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginTop: 6, marginBottom: 4 }}>{t}</p>);
    const marketsCount = new Set(locations.map(l => locationAnalyses[l.id]?.market_tier).filter(Boolean)).size;
    const anyMissingZip = locations.some(l => !l.zip);
    const effGM = effectiveMRR > 0 ? 1 - effectiveCost / effectiveMRR : 0;

    const v3Sections = [
      { id: 'client',     label: 'Client',            value: recipientBiz || 'Not set', status: recipientBiz ? 'ok' : 'empty' },
      { id: 'basis',      label: 'Service basis',     value: `${selectedPkg?.name || '—'} · ${contractTerm} mo`, status: selectedPkg ? 'ok' : 'empty' },
      { id: 'locations',  label: 'Locations',         value: `${locations.length} location${locations.length === 1 ? '' : 's'}${marketsCount ? ` · ${marketsCount} market${marketsCount === 1 ? '' : 's'}` : ''}`, status: anyMissingZip ? 'warn' : 'ok' },
      { id: 'aggregate',  label: 'Aggregate counts',  value: `${totalUsers} users · ${totalWS} wks · ${totalSrv} srv`, status: 'faint' },
      { id: 'products',   label: 'Products',          value: `${selectedProducts.length} selected`, status: 'ok' },
      { id: 'discount',   label: 'Multi-site discount', value: multiDiscRate > 0 ? `${(multiDiscRate * 100).toFixed(0)}% applied${multiDiscRateCapped ? ' (GM floor)' : ''}` : '—', status: 'ok' },
      { id: 'onboarding', label: 'Onboarding',        value: fmt$0(obIncentive?.effectiveFee ?? onboarding), status: 'ok' },
      { id: 'pricing',    label: 'Pricing',           value: `${fmt$0(finalMRR)}/mo`, status: 'ok' },
      { id: 'documents',  label: 'Documents & send',  value: existingQuote?.id ? 'Docs · pay' : 'Save first', status: 'ok' },
      { id: 'terms',      label: 'Special terms',     value: `${addendums.length} addendum${addendums.length === 1 ? '' : 's'}`, status: addendums.length ? 'ok' : 'empty' },
      { id: 'engagement', label: 'Engagement',        value: existingQuote?.id ? 'View activity' : 'Not sent', status: 'ok' },
      { id: 'history',    label: 'History',           value: existingQuote?.id ? 'Versions' : '—', status: existingQuote?.id ? 'ok' : 'empty' },
    ];

    const v3Header = (
      <QuoteHeader id={existingQuote?.quote_number || 'New multi-site quote'} title={proposalName || recipientBiz || 'Multi-Site IT quote'} status={quoteStatus} statusVariant={statusVariant}
        extraPills={[{ variant: 'blue', label: `${locations.length}-site` }, ...(hubDealId ? [{ variant: 'green', label: 'HubSpot linked' }] : [])]} savedLabel={saving ? 'Saving…' : (saveMsg || undefined)}>
        <V3Button variant="secondary" size="sm" onClick={() => setSoPreviewModalOpen(true)} disabled={!existingQuote?.id}>Preview SO</V3Button>
        <V3Button variant="dark" size="sm" onClick={saveQuote} disabled={saving || isReadOnly || !recipientBiz.trim()}>{saving ? 'Saving…' : 'Save'}</V3Button>
      </QuoteHeader>
    );

    const v3Rail = (
      <>
        <LiveTotalCard headline={fmt$0(finalMRR)} rows={[
          { label: 'Onboarding',     value: fmt$0(obIncentive?.effectiveFee ?? onboarding) },
          { label: 'Contract value', value: fmt$0(contractValue) },
          { label: 'Locations',      value: String(locations.length) },
          { label: 'Gross margin',   value: fmtPct(effGM), color: v3.color.moneyPositive },
        ]} />
        <RailCard title="Cost model — monthly">
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}><span style={{ color: v3.color.muted }}>Effective cost</span><span style={{ fontFamily: v3.font.mono, fontWeight: 600 }}>{fmt$0(effectiveCost)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 6px', background: gmBg(effGM), borderRadius: 4, marginTop: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: gmColor(effGM) }}>Implied Gross Margin</span>
            <span style={{ fontSize: 13, fontWeight: 700, fontFamily: v3.font.mono, color: gmColor(effGM) }}>{fmtPct(effGM)}</span>
          </div>
        </RailCard>
        {locations.length >= 2 && (
          <RailCard title="Multi-site discount">
            {multiDiscRate > 0 ? (
              <>
                <div style={{ fontSize: 13, color: v3.color.body }}>{(multiDiscRate * 100).toFixed(multiDiscRateCapped ? 2 : 0).replace(/\.?0+$/, '')}% applied · <strong>{fmt$0(multiDiscAmount)}/mo</strong> off</div>
                {multiDiscRateCapped && <div style={{ marginTop: 4, fontSize: 12, color: v3.color.status.amberText }}>{(multiDiscRateConfigured * 100).toFixed(0)}% configured — reduced by the GM floor solver.</div>}
              </>
            ) : <div style={{ fontSize: 12, color: v3.color.muted }}>Discount fully suppressed by the GM floor — review scope or price.</div>}
          </RailCard>
        )}
      </>
    );

    const renderMsFocus = () => {
      switch (activeSection) {
        case 'client':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Client')}{s2('Recipient and linked HubSpot deal.')}
            <HubSpotConnect dealId={hubDealId} dealUrl={hubDealUrl} dealName={hubDealName} description={dealDescription} onDescriptionChange={setDealDescription} quoteNumber={existingQuote?.quote_number} mrr={finalMRR} contractValue={contractValue} packageName={selectedPkg?.name} contractTerm={contractTerm} existingQuoteId={existingQuote?.id} clientName={recipientBiz}
              onConnect={full => { setHubDealId(full.dealId); setHubDealUrl(full.dealUrl); setHubDealName(full.deal.dealname); if (full.company) { if (full.company.name) setRecipientBiz(full.company.name); const addr = [full.company.address, full.company.city, full.company.state, full.company.zip].filter(Boolean).join(', '); if (addr) setRecipientAddress(addr); } else { const extracted = full.deal.dealname?.split(/\s[-–—]\s/)?.[0]?.trim(); if (extracted) setRecipientBiz(extracted); } if (full.contact) { const name = [full.contact.firstname, full.contact.lastname].filter(Boolean).join(' '); if (name) setRecipientContact(name); if (full.contact.email) setRecipientEmail(full.contact.email); if (full.contact.phone) setRecipientPhone(full.contact.phone); if (full.contact.mobilephone) setRecipientMobilePhone(full.contact.mobilephone); if (full.contactId) setHubContactId(full.contactId); if (full.companyId) setHubCompanyId(full.companyId); setHubspotSnapshot(captureHubspotSnapshot(full)); } if (!proposalName && full.deal.dealname) setProposalName(`FerrumIT Multi-Site IT — ${full.company?.name || full.deal.dealname}`); }}
              onDisconnect={() => { setHubDealId(''); setHubDealUrl(''); setHubDealName(''); }} />
            <SPTConnect proposalId={sptProposalId} quoteId={existingQuote?.id} clientName={recipientBiz} quoteNumber={existingQuote?.quote_number} settings={settings} onConnect={(pid) => setSptProposalId(pid)} onDisconnect={() => setSptProposalId(null)} />
            <SubCard eyebrow="Proposal Details">
              <Fld lbl="Assigned Rep"><RepSelector repId={repId} setRepId={setRepId} teamMembers={teamMembers} padding="4px 7px" fontSize={10} includeTextColor={false} /></Fld>
              <Fld lbl="Proposal Name"><TI value={proposalName} onChange={setProposalName} placeholder="FerrumIT Multi-Site IT — Acme Corp" /></Fld>
              <Grid2><Fld lbl="Business Name *"><TI value={recipientBiz} onChange={setRecipientBiz} /></Fld><Fld lbl="Contact Name"><TI value={recipientContact} onChange={setRecipientContact} /></Fld></Grid2>
              <Fld lbl="Contact Email"><TI value={recipientEmail} onChange={setRecipientEmail} /></Fld>
              <Grid2><Fld lbl="Office Phone"><TI value={recipientPhone} onChange={setRecipientPhone} placeholder="(555) 123-4567"/></Fld><Fld lbl="Mobile Phone"><TI value={recipientMobilePhone} onChange={setRecipientMobilePhone} placeholder="(555) 765-4321"/></Fld></Grid2>
              {(hubContactId || hubCompanyId) && (<div style={{ marginTop: 8 }}><V3Button variant="secondary" size="sm" onClick={pushAllToHubspot} disabled={hubPushing}>{hubPushing ? '⏳ Pushing…' : '↑ Push to HubSpot'}</V3Button>{hubPushMsg && <span style={{ fontSize: 11, color: v3.color.muted, marginLeft: 8 }}>{hubPushMsg}</span>}</div>)}
              <div style={{ marginTop: 10 }}><Fld lbl="CC on Approval (comma-separated)"><TI value={notificationCc} onChange={setNotificationCc} placeholder="alice@ferrumit.com, bob@ferrumit.com"/></Fld></div>
            </SubCard></div>);
        case 'basis':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Service basis')}{s2('Package (applied to all locations), contract term, and risk profile.')}
            <SubCard eyebrow="Package & Contract"><div style={{ fontSize:9, color:'#6b7280', marginBottom:6 }}>Same package applies to all locations</div>
              {packages.map(p => (<div key={p.id} onClick={() => setSelectedPkg(p)} style={{ padding:'5px 7px', borderRadius:4, cursor:'pointer', marginBottom:2, border:`${selectedPkg?.id===p.id?'2':'1'}px solid ${selectedPkg?.id===p.id?'#2563eb':'#e5e7eb'}`, background:selectedPkg?.id===p.id?'#eff6ff':'white' }}><div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}><span style={{ fontSize:10, fontWeight:700, color:selectedPkg?.id===p.id?'#1e40af':'#374151' }}>{p.name}</span><span style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#6b7280', background:'#f3f4f6', padding:'1px 4px', borderRadius:3 }}>${p.ws_rate}/WS · ${p.user_rate}/US · {fmt$(p.location_rate)}/LOC</span></div></div>))}
              <Fld lbl="Contract Term" s={{ marginTop:8 }}><SI v={contractTerm} s={v => setContractTerm(+v)} opts={getContractTermOptions(settings)} /></Fld>
            </SubCard>
            <SubCard eyebrow="Risk & Compliance"><Grid2><Fld lbl="Threat Profile" sub="oversight tier"><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ flex: 1 }}><SI v={industryRisk} s={v => setIndustryRisk(v)} opts={[['none','None'],['low','Standard'],['medium','Elevated'],['high','Critical']]} /></div><ThreatProfileInfo /></div></Fld><Fld lbl="Compliance" sub="adds program product"><SI v={compliance} s={v => setCompliance(v)} opts={[['none','None'],['hipaa','HIPAA'],['soc2','SOC 2'],['pci','PCI DSS'],['cmmc','CMMC']]} /></Fld></Grid2></SubCard>
          </div>);
        case 'locations':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Locations')}{s2('Each location is priced against its own market. The multi-site discount scales with the count.')}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
              {[['Locations', locations.length], ['Total users', totalUsers], ['Total workstations', totalWS]].map(([l, n]) => (
                <div key={l} style={{ background: v3.color.cardAlt, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, padding: '12px 14px' }}><div style={{ fontSize: 11, color: v3.color.muted, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 600 }}>{l}</div><div style={{ fontFamily: v3.font.mono, fontSize: 22, fontWeight: 600, color: v3.color.navy, marginTop: 4 }}>{n}</div></div>
              ))}
            </div>
            {locations.length >= 2 && multiDiscRate > 0 && (<div style={{ fontSize: v3.font.size.small, color: multiDiscRateCapped ? v3.color.status.amberText : v3.color.status.greenText }}>{multiDiscRateCapped ? `${(multiDiscRateConfigured*100).toFixed(0)}% configured · ${(multiDiscRate*100).toFixed(2).replace(/\.?0+$/, '')}% applied (GM floor)` : `${(multiDiscRate*100).toFixed(0)}% multi-location discount applied`}</div>)}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {locations.map((l) => { const analysis = locationAnalyses[l.id]; const res = locationResults.find(r => r.loc.id === l.id)?.result; return (
                <div key={l.id} style={{ background: v3.color.card, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.card, padding: '12px 14px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', background: TYPE_COLOR[l.type], flexShrink:0 }} />
                    <div style={{ flex:1, minWidth:0 }}><div style={{ fontSize:14, fontWeight:700, color:v3.color.navy, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.name}</div><div style={{ fontSize:12, color:v3.color.muted }}>{[l.city, l.state].filter(Boolean).join(', ') || l.zip || 'No address'} · {l.users} users · {l.workstations} WS{analysis && ` · ${analysis.market_tier}`}{locLoading[l.id] && ' · analyzing…'}</div></div>
                    <div style={{ textAlign:'right', flexShrink:0 }}>{res && <div style={{ fontSize:14, fontWeight:700, fontFamily:v3.font.mono, color:v3.color.navy }}>{fmt$0(res.mrr)}/mo</div>}<span style={{ fontSize:10, fontWeight:700, color: TYPE_COLOR[l.type], background: TYPE_BG[l.type], padding:'2px 6px', borderRadius:3 }}>{LOCATION_TYPES[l.type]}</span></div>
                  </div>
                  <div style={{ display:'flex', gap:6, marginTop:10 }}>
                    <V3Button variant="secondary" size="sm" onClick={() => openEditLoc(l)}>Edit</V3Button>
                    {locations.length > 1 && <V3Button variant="ghost" size="sm" onClick={() => removeLoc(l.id)}>✕ Remove</V3Button>}
                  </div>
                </div>); })}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <V3Button variant="dark" size="sm" onClick={openAddLoc}>+ Add location</V3Button>
              {locations.length >= 2 && <V3Button variant="ghost" size="sm" onClick={() => setShowConvertModal(true)}>Convert to single-location IT quote</V3Button>}
            </div>
          </div>);
        case 'aggregate':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Aggregate counts')}{s2('Derived, read-only — the sum of every location. Edit counts on each location.')}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
              {[['Total users', totalUsers], ['Total workstations', totalWS], ['Total servers', totalSrv], ['Total domains', totalDomains], ['Locations', locations.length], ['Markets', marketsCount]].map(([l, n]) => (
                <div key={l} style={{ background: v3.color.cardAlt, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, padding: '14px 16px' }}><div style={{ fontSize: 11, color: v3.color.muted, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 600 }}>{l}</div><div style={{ fontFamily: v3.font.mono, fontSize: 24, fontWeight: 600, color: v3.color.navy, marginTop: 4 }}>{n}</div></div>
              ))}
            </div>
          </div>);
        case 'products':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Products')}{s2('Applied once across all locations — qty scales to total users / workstations.')}
            {selectedPkg && (packageIncludes.length > 0 || includesLoading) && (
              <PackageIncludes packageName={selectedPkg.name} includes={packageIncludes} excludedIds={excludedIncludeIds} loading={includesLoading} includedItemsCost={totalIncludesCost} includeSwaps={includeSwaps} availableProducts={products}
                onToggleExclude={(includeId) => { setExcludedIncludeIds(prev => { const wasExcluded = prev.includes(includeId); if (wasExcluded) { setIncludeSwaps(swaps => { if (!(includeId in swaps)) return swaps; const next = { ...swaps }; delete next[includeId]; return next; }); return prev.filter(id => id !== includeId); } return [...prev, includeId]; }); }}
                onSwap={(includeId, substituteProductId) => { setExcludedIncludeIds(prev => prev.includes(includeId) ? prev : [...prev, includeId]); setIncludeSwaps(prev => ({ ...prev, [includeId]: substituteProductId })); }}
                onUnswap={(includeId) => { setIncludeSwaps(prev => { if (!(includeId in prev)) return prev; const next = { ...prev }; delete next[includeId]; return next; }); }} />
            )}
            <SubCard eyebrow="Add-on Products">
              {Object.entries(productsByCategory).map(([cat, catProds]) => { const excludedSet = new Set(excludedIncludeIds || []); const activeIncludeProductIds = new Set(packageIncludes.filter(i => !excludedSet.has(i.id)).map(i => i.product_id)); const swapSubIds = new Set(Object.values(includeSwaps || {}).filter(Boolean)); const visibleProds = catProds.filter(p => !activeIncludeProductIds.has(p.id) && !swapSubIds.has(p.id) && !p.internal_only); if (visibleProds.length === 0) return null; return (
                <div key={cat} style={{ marginBottom:8 }}><div style={{ fontSize:8, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', color:'#9ca3af', marginBottom:4 }}>{cat}</div>{visibleProds.map(p => { const sel = selectedProducts.includes(p.id); const qty = parseInt(manualQuantities?.[p.id] || 0); return (
                  <div key={p.id} onClick={() => toggleProduct(p.id)} style={{ display:'flex', alignItems:'center', gap:6, padding:'4px 6px', borderRadius:4, cursor:'pointer', marginBottom:2, border:`1px solid ${sel?'#93c5fd':'#e5e7eb'}`, background:sel?'#eff6ff':'white' }}>
                    <div style={{ width:20, height:12, borderRadius:6, background:sel?'#2563eb':'#d1d5db', position:'relative', flexShrink:0 }}><div style={{ position:'absolute', top:2, left:sel?9:2, width:8, height:8, borderRadius:'50%', background:'white', transition:'left .1s' }} /></div>
                    <div style={{ flex:1, minWidth:0 }}><div style={{ fontSize:9, fontWeight:600, color:sel?'#1e40af':'#374151' }}>{p.name}</div></div>
                    {sel && p.qty_driver === 'manual' && (<div onClick={e=>e.stopPropagation()} style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:0 }}><input type="number" min="0" value={qty || ''} placeholder="qty" onChange={e=>{ const next = e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0); setManualQuantities(prev => ({ ...prev, [p.id]: next })); }} style={{ width:48, padding:'1px 4px', border:`1px solid ${qty > 0 ? '#93c5fd' : '#fbbf24'}`, borderRadius:3, fontSize:9, fontFamily:'DM Mono, monospace', textAlign:'center', outline:'none', background: qty > 0 ? 'white' : '#fffbeb' }}/>{qty === 0 && <span style={{ fontSize:7, color:'#92400e', fontWeight:600, marginTop:1 }}>set qty</span>}</div>)}
                    <div style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#6b7280' }}>${p.sell_price}/{p.qty_driver === 'manual' ? 'lic' : p.qty_driver}</div>
                  </div>); })}</div>); })}
            </SubCard>
          </div>);
        case 'discount':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Multi-site discount')}{s2('Auto-derived from the number of locations and capped by the gross-margin floor. There is no manual rate — adjust locations, scope, or price to change it.')}
            <div style={{ background: v3.color.inkBg, border: `1px solid ${v3.color.inkBorder}`, borderRadius: v3.radius.card, padding: 20 }}>
              {locations.length < 2 ? s2('Add a second location to qualify for the multi-site discount.') : multiDiscRate > 0 ? (<>
                <div style={{ fontFamily: v3.font.mono, fontSize: 26, fontWeight: 600, color: v3.color.navy }}>{(multiDiscRate * 100).toFixed(multiDiscRateCapped ? 2 : 0).replace(/\.?0+$/, '')}%<span style={{ fontSize: 14, color: v3.color.muted, marginLeft: 6 }}>applied</span></div>
                <div style={{ marginTop: 6, fontSize: v3.font.size.body, color: v3.color.bodySoft }}>Saves <strong>{fmt$0(multiDiscAmount)}/mo</strong> across {locations.length} locations.</div>
                {multiDiscRateCapped && <div style={{ marginTop: 10, padding: '8px 12px', background: v3.color.status.amberBg, borderRadius: v3.radius.input, fontSize: v3.font.size.small, color: v3.color.status.amberText }}>{(multiDiscRateConfigured * 100).toFixed(0)}% is configured for this count, but the GM-floor solver reduced it to protect margin. Add cost, raise price, or accept the smaller discount.</div>}
              </>) : <div style={{ fontSize: v3.font.size.body, color: v3.color.status.amberText }}>The discount is fully suppressed by the GM floor on this deal — the margin can't support it. Review scope or price.</div>}
            </div>
          </div>);
        case 'onboarding':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Onboarding')}{s2('Incentive, discounts, and the effective date.')}
            {onboarding > 0 && (<OnboardingIncentive fee={onboarding} marketTier={locationResults.length > 0 && locationAnalyses[locations[0]?.id] ? locationAnalyses[locations[0].id].market_tier : 'standard'} contractTerm={contractTerm} initialValue={obIncentive} onChange={inc => { const sig = inc ? `${inc.mode || 'none'}:${inc.effectiveFee ?? ''}:${inc.discountPct ?? ''}:${inc.splitMonths ?? ''}` : 'null'; const prevSig = obIncentive ? `${obIncentive.mode || 'none'}:${obIncentive.effectiveFee ?? ''}:${obIncentive.discountPct ?? ''}:${obIncentive.splitMonths ?? ''}` : 'null'; if (sig === prevSig) return; setObIncentive(inc); }} />)}
            <ManualDiscounts value={manualDiscounts} onChange={setManualDiscounts} baseMRR={finalMRRPreDiscount} />
            <OneTimeDiscounts value={oneTimeDiscounts} onChange={setOneTimeDiscounts} bases={oneTimeBases} />
            <AgreementEffectiveDate value={effectiveDate} onChange={setEffectiveDate} />
          </div>);
        case 'pricing':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Pricing')}{s2('Combined across all locations.')}
            <div style={{ background: v3.color.card, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.card, padding: 20 }}>
              <LI lbl="Location subtotal (all sites)" v={totalLocationMRR} bold/>
              {multiDiscRate > 0 && <LI lbl={`Multi-location discount (${(multiDiscRate*100).toFixed(0)}% · ${locations.length} sites)`} v={-multiDiscAmount} ind/>}
              {itsoMRR > 0 && <LI lbl="ITSO Advisory" v={itsoMRR} ind/>}
              {addonRevenue > 0 && <LI lbl="Add-on products" v={addonRevenue} ind/>}
              <LI lbl="Final monthly recurring" v={finalMRR} hi/>
              <div style={{ height: 1, background: v3.color.divider, margin: '10px 0' }} />
              <LI lbl="One-time (NRC)" v={obIncentive?.effectiveFee ?? onboarding} ind/>
              <LI lbl="Effective monthly cost" v={effectiveCost} ind muted/>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px', background: gmBg(effGM), borderRadius: 4, marginTop: 6 }}><span style={{ fontSize: 12, fontWeight: 700, color: gmColor(effGM) }}>Implied Gross Margin</span><span style={{ fontSize: 14, fontWeight: 700, fontFamily: v3.font.mono, color: gmColor(effGM) }}>{fmtPct(effGM)}</span></div>
              <LI lbl="Total Contract Value" v={contractValue} bold/>
            </div>
          </div>);
        case 'documents':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Documents & send')}{s2('Preview and send the Multi-Site Service Order, and collect prepayment.')}
            <QuoteDocuments quoteId={existingQuote?.id} />
            <DocumentsPanel analysis={locationResults[0]?.analysis || null} settings={settings} clientName={recipientBiz} recipientContact={recipientContact} quoteId={existingQuote?.id} quoteNumber={existingQuote?.quote_number} sptProposalId={sptProposalId} onSPTLinked={(pid) => setSptProposalId(pid)} inputs={{ users: totalUsers, workstations: totalWS, locations: locations.length, contractTerm, compliance, selectedProducts: [] }} pkg={selectedPkg} products={products} complianceKey={complianceKey} result={{ finalMRR, onboarding }} obIncentive={obIncentive} quoteType="multi-site-managed-it" clientEmail={recipientEmail} prepayAmount={oneTimeResolved.finalNRC} hubspotDealId={hubDealId} onOpenAssumptions={() => setShowAssumptions(true)} onOpenAddendums={() => setAddendumsModalOpen(true)} addendumsCount={addendums.length} onOpenServiceOrderPreview={() => setSoPreviewModalOpen(true)} assumptionsSaved={assumptionsComplete} />
            {existingQuote?.id && (<div><V3Button variant="secondary" size="sm" onClick={() => setShowEtfModal(true)}>💰 Calculate ETF →</V3Button></div>)}
          </div>);
        case 'terms':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Special terms')}{s2('Addendums — including the covered-locations clause.')}<div style={{ fontSize: v3.font.size.body, color: v3.color.muted }}>{addendums.length} addendum{addendums.length === 1 ? '' : 's'} attached.</div><div><V3Button variant="secondary" size="sm" onClick={() => setAddendumsModalOpen(true)}>Edit addendums</V3Button></div></div>);
        case 'engagement':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Engagement')}{s2('Client interaction with the sent quote (live).')}<EngagementPanel quoteId={existingQuote?.id} /><QuoteNotes quoteId={existingQuote?.id} quoteNumber={existingQuote?.quote_number} clientName={recipientBiz} hubDealId={hubDealId} /></div>);
        case 'history':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('History')}{s2('Saved versions of this quote.')}<QuoteHistory quoteId={existingQuote?.id} /></div>);
        default:
          return null;
      }
    };

    const locked = isQuoteLocked(existingQuote);
    return (
      <div style={{ height: '100%', overflowY: 'auto' }}>
        {isReadOnly && (<div style={{ padding: '8px 24px', background: v3.color.status.amberBg, color: v3.color.status.amberText, fontSize: 12, fontWeight: 600 }}>🔒 View only — your account no longer has permission to edit Multi-Site IT quotes.</div>)}
        {locked && (
          <LockedQuoteBanner notes={dealDescription} onNotesChange={setDealDescription} onSave={saveQuote} saving={saving} reason={QUOTE_LOCK_REASON} />
        )}
        <WorkspaceShell header={v3Header} sections={v3Sections} activeId={activeSection} onSelect={setMsFocusedSection} rail={v3Rail}>
          {locked && activeSection !== 'documents'
            ? <div style={{ pointerEvents: 'none', opacity: 0.55 }} aria-disabled="true">{renderMsFocus()}</div>
            : renderMsFocus()}
        </WorkspaceShell>
        {showConvertModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:400 }}>
            <div style={{ background:'white', borderRadius:10, padding:28, width:440, boxShadow: v3.shadow.modal }}>
              <h3 style={{ fontSize:15, fontWeight:700, color:'#0f1e3c', margin:'0 0 8px' }}>Convert to Single Location</h3>
              <p style={{ fontSize:12, color:'#6b7280', margin:'0 0 16px' }}>This will open a new standard IT quote using <strong>{locations[0]?.name || 'Location 1'}</strong> only. All other locations will be discarded. The multi-site quote stays in your history.</p>
              <div style={{ background:'#fef3c7', borderRadius:5, padding:'8px 10px', fontSize:11, color:'#92400e', marginBottom:16 }}>⚠ The multi-location discount will not apply to a single-location quote.</div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}><V3Button variant="secondary" size="sm" onClick={() => setShowConvertModal(false)}>Cancel</V3Button><V3Button variant="dark" size="sm" onClick={() => { setShowConvertModal(false); convertToSingle(); }}>Convert →</V3Button></div>
            </div>
          </div>
        )}
        {showLocModal && editingLoc && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:400, overflowY:'auto', padding:'20px 0' }}>
            <div style={{ background:'white', borderRadius:10, padding:24, width:500, boxShadow: v3.shadow.modal }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}><h3 style={{ fontSize:14, fontWeight:700, color:'#0f1e3c', margin:0 }}>{locations.find(l => l.id === editingLoc.id) ? 'Edit Location' : 'Add Location'}</h3><button onClick={() => setShowLocModal(false)} style={{ background:'none', border:'none', fontSize:20, color:'#6b7280', cursor:'pointer' }}>×</button></div>
              <div style={{ marginBottom:14 }}><label style={{ display:'block', fontSize:10, fontWeight:700, color:'#374151', marginBottom:6, textTransform:'uppercase', letterSpacing:'.05em' }}>Location Type</label><div style={{ display:'flex', gap:8 }}>{Object.entries(LOCATION_TYPES).map(([key, label]) => (<button key={key} onClick={() => setEditingLoc(e => ({ ...e, type: key }))} style={{ flex:1, padding:'8px 10px', border:`2px solid ${editingLoc.type === key ? TYPE_COLOR[key] : '#e5e7eb'}`, borderRadius:6, background: editingLoc.type === key ? TYPE_BG[key] : 'white', cursor:'pointer', textAlign:'left' }}><div style={{ fontSize:11, fontWeight:700, color: editingLoc.type === key ? TYPE_COLOR[key] : '#374151' }}>{label}</div><div style={{ fontSize:9, color:'#6b7280', marginTop:2 }}>{key === 'standard' ? 'Full managed — all services' : 'No network layer'}</div></button>))}</div><div style={{ fontSize:9, color:'#9ca3af', marginTop:6 }}>{TYPE_DESC[editingLoc.type]?.replace('$location_fee', fmt$(selectedPkg?.location_rate || 150))}</div></div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
                <div style={{ gridColumn:'1/-1' }}><label style={{ display:'block', fontSize:10, fontWeight:600, color:'#374151', marginBottom:3 }}>Location Name *</label><input value={editingLoc.name} onChange={e => setEditingLoc(l => ({ ...l, name: e.target.value }))} placeholder="e.g. Corporate HQ, Chicago Branch" style={{ width:'100%', padding:'6px 8px', border:`1px solid ${!editingLoc.name.trim() ? '#fca5a5' : '#d1d5db'}`, borderRadius:5, fontSize:12, outline:'none' }} /></div>
                <div style={{ gridColumn:'1/-1' }}><label style={{ display:'block', fontSize:10, fontWeight:600, color:'#374151', marginBottom:3 }}>Street Address</label><input value={editingLoc.address} onChange={e => setEditingLoc(l => ({ ...l, address: e.target.value }))} placeholder="123 Main St" style={{ width:'100%', padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:5, fontSize:12, outline:'none' }} /></div>
                <div><label style={{ display:'block', fontSize:10, fontWeight:600, color:'#374151', marginBottom:3 }}>City</label><input value={editingLoc.city} onChange={e => setEditingLoc(l => ({ ...l, city: e.target.value }))} style={{ width:'100%', padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:5, fontSize:12, outline:'none' }} /></div>
                <div><label style={{ display:'block', fontSize:10, fontWeight:600, color:'#374151', marginBottom:3 }}>State</label><input value={editingLoc.state} onChange={e => setEditingLoc(l => ({ ...l, state: e.target.value.toUpperCase().slice(0,2) }))} placeholder="IL" style={{ width:'100%', padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:5, fontSize:12, outline:'none', textTransform:'uppercase' }} /></div>
                <div style={{ gridColumn:'1/-1' }}><label style={{ display:'block', fontSize:10, fontWeight:600, color:'#374151', marginBottom:3 }}>ZIP Code (drives market rate analysis)</label><input value={editingLoc.zip} onChange={e => setEditingLoc(l => ({ ...l, zip: e.target.value.replace(/\D/g,'').slice(0,5) }))} placeholder="60601" style={{ width:'100%', padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:5, fontSize:12, outline:'none', fontFamily:'DM Mono, monospace', fontWeight:600 }} />{locLoading[editingLoc.id] && <div style={{ fontSize:9, color:'#2563eb', marginTop:3 }}>Analyzing market...</div>}{locationAnalyses[editingLoc.id] && !locLoading[editingLoc.id] && (<div style={{ fontSize:9, color:'#166534', marginTop:3 }}>✓ {locationAnalyses[editingLoc.id].city}, {locationAnalyses[editingLoc.id].state} — {locationAnalyses[editingLoc.id].market_tier} market · {locationAnalyses[editingLoc.id].pricing_multiplier}× multiplier</div>)}</div>
              </div>
              <div style={{ fontSize:10, fontWeight:700, color:'#374151', marginBottom:6, textTransform:'uppercase', letterSpacing:'.05em' }}>People & Devices</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:16 }}>{[['users','Users'],['workstations','Workstations'],['servers','Servers'],['endpoints','Endpoints'],['mobileDevices','Mobile Devices'],['domains','Domains']].map(([key, label]) => (<div key={key}><label style={{ display:'block', fontSize:10, fontWeight:600, color:'#374151', marginBottom:3 }}>{label}</label><div style={{ display:'flex', alignItems:'center', gap:4 }}><input type="number" min="0" value={editingLoc[key] || 0} onChange={e => setEditingLoc(l => ({ ...l, [key]: parseInt(e.target.value)||0 }))} style={{ flex:1, minWidth:0, padding:'5px 7px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', color:'#1e3a5f', background:'#eff6ff', fontWeight:600, outline:'none' }} />{key === 'domains' && <DomainsInfo />}</div></div>))}</div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}><V3Button variant="secondary" size="sm" onClick={() => setShowLocModal(false)}>Cancel</V3Button><V3Button variant="dark" size="sm" onClick={saveLocModal} disabled={!editingLoc.name.trim()}>Save Location</V3Button></div>
            </div>
          </div>
        )}
        {showAssumptions && (<AssumptionsModal onClose={() => setShowAssumptions(false)} quoteId={existingQuote?.id} quoteNumber={existingQuote?.quote_number} clientName={recipientBiz} recipientContact={recipientContact} inputs={{ users: totalUsers, workstations: totalWS, locations: locations.length, contractTerm, compliance }} pkg={selectedPkg} products={products} settings={settings} complianceKey={complianceKey} onSave={(a) => { setAssumptionsStory?.(a.story || ''); }} />)}
        <AddendumsModal open={addendumsModalOpen} onClose={() => setAddendumsModalOpen(false)} addendums={addendums} templates={addendumTemplates} onSave={setAddendums} />
        <ServiceOrderPreviewModal open={soPreviewModalOpen} onClose={() => setSoPreviewModalOpen(false)} quoteId={existingQuote?.id} docType="multisite_quote" clientBiz={recipientBiz} />
        {showEtfModal && (<ETFCalculatorModal onClose={() => setShowEtfModal(false)} defaultMRR={finalMRR || ''} defaultTerm={contractTerm || 36} defaultClientName={recipientBiz} defaultClientAddress={recipientAddress} defaultClientContact={recipientContact} defaultClientEmail={recipientEmail} defaultContractReference={existingQuote?.quote_number || ''} quoteId={existingQuote?.id} hubDealId={hubDealId} />)}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Convert to single modal ── */}
      {showConvertModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:400 }}>
          <div style={{ background:'white', borderRadius:10, padding:28, width:440, boxShadow:'0 8px 32px rgba(0,0,0,0.15)' }}>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#0f1e3c', margin:'0 0 8px' }}>Convert to Single Location</h3>
            <p style={{ fontSize:12, color:'#6b7280', margin:'0 0 16px' }}>
              This will open a new standard IT quote using <strong>{locations[0]?.name || 'Location 1'}</strong> only.
              All other locations will be discarded. The multi-site quote stays in your history.
            </p>
            <div style={{ background:'#fef3c7', borderRadius:5, padding:'8px 10px', fontSize:11, color:'#92400e', marginBottom:16 }}>
              ⚠ The multi-location discount will not apply to a single-location quote.
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => setShowConvertModal(false)}
                style={{ padding:'7px 14px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:5, fontSize:12, color:'#374151', cursor:'pointer' }}>Cancel</button>
              <button onClick={() => { setShowConvertModal(false); convertToSingle(); }}
                style={{ padding:'7px 18px', background:'#0f1e3c', color:'white', border:'none', borderRadius:5, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                Convert →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Location modal ── */}
      {showLocModal && editingLoc && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:400, overflowY:'auto', padding:'20px 0' }}>
          <div style={{ background:'white', borderRadius:10, padding:24, width:500, boxShadow:'0 8px 32px rgba(0,0,0,0.15)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h3 style={{ fontSize:14, fontWeight:700, color:'#0f1e3c', margin:0 }}>
                {locations.find(l => l.id === editingLoc.id) ? 'Edit Location' : 'Add Location'}
              </h3>
              <button onClick={() => setShowLocModal(false)} style={{ background:'none', border:'none', fontSize:20, color:'#6b7280', cursor:'pointer' }}>×</button>
            </div>

            {/* Location type */}
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:10, fontWeight:700, color:'#374151', marginBottom:6, textTransform:'uppercase', letterSpacing:'.05em' }}>Location Type</label>
              <div style={{ display:'flex', gap:8 }}>
                {Object.entries(LOCATION_TYPES).map(([key, label]) => (
                  <button key={key} onClick={() => setEditingLoc(e => ({ ...e, type: key }))}
                    style={{ flex:1, padding:'8px 10px', border:`2px solid ${editingLoc.type === key ? TYPE_COLOR[key] : '#e5e7eb'}`,
                      borderRadius:6, background: editingLoc.type === key ? TYPE_BG[key] : 'white', cursor:'pointer', textAlign:'left' }}>
                    <div style={{ fontSize:11, fontWeight:700, color: editingLoc.type === key ? TYPE_COLOR[key] : '#374151' }}>{label}</div>
                    <div style={{ fontSize:9, color:'#6b7280', marginTop:2 }}>
                      {key === 'standard' ? 'Full managed — all services' : 'No network layer'}
                    </div>
                  </button>
                ))}
              </div>
              <div style={{ fontSize:9, color:'#9ca3af', marginTop:6 }}>{TYPE_DESC[editingLoc.type]?.replace('$location_fee', fmt$(selectedPkg?.location_rate || 150))}</div>
            </div>

            {/* Name & address */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={{ display:'block', fontSize:10, fontWeight:600, color:'#374151', marginBottom:3 }}>Location Name *</label>
                <input value={editingLoc.name} onChange={e => setEditingLoc(l => ({ ...l, name: e.target.value }))}
                  placeholder="e.g. Corporate HQ, Chicago Branch, WeWork Austin"
                  style={{ width:'100%', padding:'6px 8px', border:`1px solid ${!editingLoc.name.trim() ? '#fca5a5' : '#d1d5db'}`, borderRadius:5, fontSize:12, outline:'none' }} />
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={{ display:'block', fontSize:10, fontWeight:600, color:'#374151', marginBottom:3 }}>Street Address</label>
                <input value={editingLoc.address} onChange={e => setEditingLoc(l => ({ ...l, address: e.target.value }))}
                  placeholder="123 Main St"
                  style={{ width:'100%', padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:5, fontSize:12, outline:'none' }} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:10, fontWeight:600, color:'#374151', marginBottom:3 }}>City</label>
                <input value={editingLoc.city} onChange={e => setEditingLoc(l => ({ ...l, city: e.target.value }))}
                  style={{ width:'100%', padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:5, fontSize:12, outline:'none' }} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:10, fontWeight:600, color:'#374151', marginBottom:3 }}>State</label>
                <input value={editingLoc.state} onChange={e => setEditingLoc(l => ({ ...l, state: e.target.value.toUpperCase().slice(0,2) }))}
                  placeholder="IL" style={{ width:'100%', padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:5, fontSize:12, outline:'none', textTransform:'uppercase' }} />
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={{ display:'block', fontSize:10, fontWeight:600, color:'#374151', marginBottom:3 }}>ZIP Code (drives market rate analysis)</label>
                <input value={editingLoc.zip} onChange={e => setEditingLoc(l => ({ ...l, zip: e.target.value.replace(/\D/g,'').slice(0,5) }))}
                  placeholder="60601"
                  style={{ width:'100%', padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:5, fontSize:12, outline:'none', fontFamily:'DM Mono, monospace', fontWeight:600 }} />
                {locLoading[editingLoc.id] && <div style={{ fontSize:9, color:'#2563eb', marginTop:3 }}>Analyzing market...</div>}
                {locationAnalyses[editingLoc.id] && !locLoading[editingLoc.id] && (
                  <div style={{ fontSize:9, color:'#166534', marginTop:3 }}>
                    ✓ {locationAnalyses[editingLoc.id].city}, {locationAnalyses[editingLoc.id].state} — {locationAnalyses[editingLoc.id].market_tier} market · {locationAnalyses[editingLoc.id].pricing_multiplier}× multiplier
                  </div>
                )}
              </div>
            </div>

            {/* Devices */}
            <div style={{ fontSize:10, fontWeight:700, color:'#374151', marginBottom:6, textTransform:'uppercase', letterSpacing:'.05em' }}>People & Devices</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:16 }}>
              {[['users','Users','Always full security stack'],['workstations','Workstations',''],['servers','Servers',''],
                ['endpoints','Endpoints',''],['mobileDevices','Mobile Devices',''],
                // v3.5.96.0: domains as a per-location field. Use case: franchises
                // where each location has its own domain (coffeeshopelgin.com,
                // coffeeshopseattle.com) and the franchisor bills domain costs
                // back to each franchisee. Slots at the end of the per-location
                // infrastructure list to match the quote-page UI order
                // (infrastructure → configuration items).
                ['domains','Domains','Per-location domains']].map(([key, label, hint]) => (
                <div key={key}>
                  <label style={{ display:'block', fontSize:10, fontWeight:600, color:'#374151', marginBottom:3 }}>
                    {label}{hint && <span style={{ fontWeight:400, color:'#9ca3af', fontSize:9 }}> — {hint}</span>}
                  </label>
                  {/* v3.5.99.7.11: DomainsInfo bubble on the domains row only.
                      Flex wrapper handles both cases (with and without bubble)
                      uniformly — for non-domain rows the bubble is null so the
                      input still fills the cell. */}
                  <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                    <input type="number" min="0" value={editingLoc[key] || 0}
                      onChange={e => setEditingLoc(l => ({ ...l, [key]: parseInt(e.target.value)||0 }))}
                      style={{ flex:1, minWidth:0, padding:'5px 7px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', color:'#1e3a5f', background:'#eff6ff', fontWeight:600, outline:'none' }} />
                    {key === 'domains' && <DomainsInfo />}
                  </div>
                </div>
              ))}
            </div>

            {/* Preview MRR */}
            {selectedPkg && (editingLoc.users > 0 || editingLoc.workstations > 0) && (() => {
              const analysis = locationAnalyses[editingLoc.id];
              const mktMult = analysis?.pricing_multiplier ?? 1;
              const res = calcLocationMRR({ location: editingLoc, pkg: selectedPkg, marketMultiplier: mktMult, settings, packageIncludes, excludedIncludes: excludedIncludeIds, includeSwaps, products, costComponents });
              return res ? (
                <div style={{ background:'#f0f4ff', borderRadius:6, padding:'8px 12px', marginBottom:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:11, color:'#374151' }}>Estimated MRR for this location</span>
                  <span style={{ fontSize:14, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f1e3c' }}>{fmt$0(res.mrr)}/mo</span>
                </div>
              ) : null;
            })()}

            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => setShowLocModal(false)}
                style={{ padding:'7px 14px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:5, fontSize:12, color:'#374151', cursor:'pointer' }}>Cancel</button>
              <button onClick={saveLocModal} disabled={!editingLoc.name.trim()}
                style={{ padding:'7px 18px', background:'#0f1e3c', color:'white', border:'none', borderRadius:5, fontSize:12, fontWeight:700, cursor:'pointer', opacity: !editingLoc.name.trim() ? 0.5 : 1 }}>
                Save Location
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display:'flex', height:'100%', overflow:'hidden' }}>

        {/* ── LEFT: Inputs ── */}
        <div style={{ width:345, flexShrink:0, borderRight:'1px solid #e5e7eb', display:'flex', flexDirection:'column', overflow:'hidden', background:'#fafafa' }}>
          <div style={{ flex:1, overflowY:'auto', padding:'12px 14px' }}>

            {/* v3.5.67: read-only banner */}
            {isReadOnly && (
              <div style={{ marginBottom:8, padding:'8px 10px', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:5, fontSize:11, color:'#92400e', fontWeight:600 }}>
                🔒 View only — your account no longer has permission to edit Multi-Site IT quotes. Contact an admin to restore access.
              </div>
            )}

            {/* HubSpot */}
            <HubSpotConnect
              dealId={hubDealId}
              dealUrl={hubDealUrl}
              dealName={hubDealName}
              description={dealDescription}
              onDescriptionChange={setDealDescription}
              quoteNumber={existingQuote?.quote_number}
              mrr={finalMRR}
              contractValue={contractValue}
              packageName={selectedPkg?.name}
              contractTerm={contractTerm}
              existingQuoteId={existingQuote?.id}
              clientName={recipientBiz}
              onConnect={full => {
                setHubDealId(full.dealId);
                setHubDealUrl(full.dealUrl);
                setHubDealName(full.deal.dealname);
                if (full.company) {
                  if (full.company.name) setRecipientBiz(full.company.name);
                  const addr = [full.company.address, full.company.city, full.company.state, full.company.zip].filter(Boolean).join(', ');
                  if (addr) setRecipientAddress(addr);
                } else {
                  const extracted = full.deal.dealname?.split(/\s[-–—]\s/)?.[0]?.trim();
                  if (extracted) setRecipientBiz(extracted);
                }
                if (full.contact) {
                  const name = [full.contact.firstname, full.contact.lastname].filter(Boolean).join(' ');
                  if (name) setRecipientContact(name);
                  if (full.contact.email) setRecipientEmail(full.contact.email);
                  if (full.contact.phone) setRecipientPhone(full.contact.phone);
                  if (full.contact.mobilephone) setRecipientMobilePhone(full.contact.mobilephone);
                  // v3.5.99.7.4: capture write-back routing IDs + original-state snapshot
                  if (full.contactId) setHubContactId(full.contactId);
                  if (full.companyId) setHubCompanyId(full.companyId);
                  setHubspotSnapshot(captureHubspotSnapshot(full));
                }
                if (!proposalName && full.deal.dealname) setProposalName(`FerrumIT Multi-Site IT — ${full.company?.name || full.deal.dealname}`);
              }}
              onDisconnect={() => { setHubDealId(''); setHubDealUrl(''); setHubDealName(''); }}
            />
            <SPTConnect
              proposalId={sptProposalId}
              quoteId={existingQuote?.id}
              clientName={recipientBiz}
              quoteNumber={existingQuote?.quote_number}
              settings={settings}
              onConnect={(pid) => setSptProposalId(pid)}
              onDisconnect={() => setSptProposalId(null)}
            />

            {/* Proposal */}
            <Sec t="Proposal Details" c="#0f1e3c">
              <Fld lbl="Assigned Rep">
                <RepSelector repId={repId} setRepId={setRepId} teamMembers={teamMembers} padding="4px 7px" fontSize={10} includeTextColor={false} />
              </Fld>
              <Fld lbl="Proposal Name"><TI value={proposalName} onChange={setProposalName} placeholder="FerrumIT Multi-Site IT — Acme Corp" /></Fld>
              <Grid2>
                <Fld lbl="Business Name *"><TI value={recipientBiz} onChange={setRecipientBiz} /></Fld>
                <Fld lbl="Contact Name"><TI value={recipientContact} onChange={setRecipientContact} /></Fld>
              </Grid2>
              <Fld lbl="Contact Email"><TI value={recipientEmail} onChange={setRecipientEmail} /></Fld>
              {/* v3.5.99.7.6 (fixes v3.5.99.7.3 miss): Office + Mobile Phone fields, auto-pulled from HubSpot via connectDeal. */}
              <Grid2>
                <Fld lbl="Office Phone"><TI value={recipientPhone} onChange={setRecipientPhone} placeholder="(555) 123-4567"/></Fld>
                <Fld lbl="Mobile Phone"><TI value={recipientMobilePhone} onChange={setRecipientMobilePhone} placeholder="(555) 765-4321"/></Fld>
              </Grid2>
              {/* v3.5.99.7.6: manual full-overwrite push to HubSpot */}
              {(hubContactId || hubCompanyId) && (
                <div style={{ marginTop: 6, padding: '6px 10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={pushAllToHubspot} disabled={hubPushing}
                    style={{ padding: '4px 10px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 3, fontSize: 10, fontWeight: 600, cursor: hubPushing ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
                    {hubPushing ? '⏳ Pushing...' : '↑ Push to HubSpot'}
                  </button>
                  <span style={{ fontSize: 9, color: '#0c4a6e' }}>Overwrites HubSpot contact + company with values above</span>
                  {hubPushMsg && <span style={{ fontSize: 10, color: '#075985', flex: 1, textAlign: 'right', minWidth: 0 }}>{hubPushMsg}</span>}
                </div>
              )}
              {/* v3.5.99.0: Internal Notification CCs — see QuotePage block for rationale */}
              <div style={{ marginTop: 10, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4 }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#92400e', marginBottom: 4 }}>🔒 Internal Notifications</div>
                <Fld lbl="CC on Approval (comma-separated)"><TI value={notificationCc} onChange={setNotificationCc} placeholder="alice@ferrumit.com, bob@ferrumit.com"/></Fld>
                <div style={{ fontSize: 9, color: '#92400e', marginTop: 4, lineHeight: 1.4 }}>Leave blank to use the global default from Admin → Notifications. Not visible to the customer.</div>
              </div>
            </Sec>

            {/* Package & Term */}
            <Sec t="Package & Contract" c="#2563eb">
              <div style={{ fontSize:9, color:'#6b7280', marginBottom:6 }}>Same package applies to all locations</div>
              {packages.map(p => (
                <div key={p.id} onClick={() => setSelectedPkg(p)}
                  style={{ padding:'5px 7px', borderRadius:4, cursor:'pointer', marginBottom:2, border:`${selectedPkg?.id===p.id?'2':'1'}px solid ${selectedPkg?.id===p.id?'#2563eb':'#e5e7eb'}`, background:selectedPkg?.id===p.id?'#eff6ff':'white' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:10, fontWeight:700, color:selectedPkg?.id===p.id?'#1e40af':'#374151' }}>{p.name}</span>
                    <span style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#6b7280', background:'#f3f4f6', padding:'1px 4px', borderRadius:3 }}>
                      ${p.ws_rate}/WS · ${p.user_rate}/US · {fmt$(p.location_rate)}/LOC
                    </span>
                  </div>
                </div>
              ))}
              {/* v3.5.86.2.1: contract term opts derived from pricing_settings
                  via getContractTermOptions(settings). The MultiSite calc
                  at line ~273 already reads contract_disc_${term} directly;
                  this brings the dropdown LABEL into agreement with the
                  math so admin changes surface in the picker immediately. */}
              <Fld lbl="Contract Term" s={{ marginTop:8 }}>
                <SI v={contractTerm} s={v => setContractTerm(+v)} opts={getContractTermOptions(settings)} />
              </Fld>
            </Sec>

            {/* Risk & Compliance */}
            <Sec t="Risk & Compliance" c="#dc2626">
              <Grid2>
                {/* v3.5.59: 'Industry Risk' renamed to 'Threat Profile';
                    Low/Medium/High → Standard/Elevated/Critical. */}
                <Fld lbl="Threat Profile" sub="oversight tier">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ flex: 1 }}><SI v={industryRisk} s={v => setIndustryRisk(v)} opts={[['none','None'],['low','Standard'],['medium','Elevated'],['high','Critical']]} /></div>
                    <ThreatProfileInfo />
                  </div>
                </Fld>
                {/* v3.5.59: Compliance expanded to 5 framework values; matching
                    Compliance Program product is auto-recommended. */}
                <Fld lbl="Compliance" sub="adds program product"><SI v={compliance} s={v => setCompliance(v)} opts={[['none','None'],['hipaa','HIPAA'],['soc2','SOC 2'],['pci','PCI DSS'],['cmmc','CMMC']]} /></Fld>
              </Grid2>
            </Sec>

            {/* Package Includes (v3.5.31) — bundled products shown above add-ons */}
            {selectedPkg && (packageIncludes.length > 0 || includesLoading) && (
              <PackageIncludes
                packageName={selectedPkg.name}
                includes={packageIncludes}
                excludedIds={excludedIncludeIds}
                loading={includesLoading}
                includedItemsCost={totalIncludesCost}
                includeSwaps={includeSwaps}
                availableProducts={products}
                onToggleExclude={(includeId) => {
                  setExcludedIncludeIds(prev => {
                    const wasExcluded = prev.includes(includeId);
                    if (wasExcluded) {
                      setIncludeSwaps(swaps => {
                        if (!(includeId in swaps)) return swaps;
                        const next = { ...swaps };
                        delete next[includeId];
                        return next;
                      });
                      return prev.filter(id => id !== includeId);
                    }
                    return [...prev, includeId];
                  });
                }}
                onSwap={(includeId, substituteProductId) => {
                  setExcludedIncludeIds(prev => prev.includes(includeId) ? prev : [...prev, includeId]);
                  setIncludeSwaps(prev => ({ ...prev, [includeId]: substituteProductId }));
                }}
                onUnswap={(includeId) => {
                  setIncludeSwaps(prev => {
                    if (!(includeId in prev)) return prev;
                    const next = { ...prev };
                    delete next[includeId];
                    return next;
                  });
                }}
              />
            )}

            {/* Add-on Products — included products are filtered out (v3.5.31)
                unless the rep has excluded them (v3.5.32). */}
            <Sec t="Add-on Products" c="#7c3aed">
              <div style={{ fontSize:9, color:'#6b7280', marginBottom:6 }}>Applied once across all locations — qty scales to total users/workstations</div>
              {Object.entries(productsByCategory).map(([cat, catProds]) => {
                const excludedSet = new Set(excludedIncludeIds || []);
                const activeIncludeProductIds = new Set(
                  packageIncludes
                    .filter(i => !excludedSet.has(i.id))
                    .map(i => i.product_id)
                );
                const swapSubIds = new Set(Object.values(includeSwaps || {}).filter(Boolean));
                const visibleProds = catProds.filter(p => !activeIncludeProductIds.has(p.id) && !swapSubIds.has(p.id) && !p.internal_only);
                if (visibleProds.length === 0) return null;
                return (
                <div key={cat} style={{ marginBottom:8 }}>
                  <div style={{ fontSize:8, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', color:'#9ca3af', marginBottom:4 }}>{cat}</div>
                  {visibleProds.map(p => {
                    const sel = selectedProducts.includes(p.id);
                    const qty = parseInt(manualQuantities?.[p.id] || 0);
                    return (
                      <div key={p.id} onClick={() => toggleProduct(p.id)}
                        style={{ display:'flex', alignItems:'center', gap:6, padding:'4px 6px', borderRadius:4, cursor:'pointer', marginBottom:2,
                          border:`1px solid ${sel?'#93c5fd':'#e5e7eb'}`, background:sel?'#eff6ff':'white' }}>
                        <div style={{ width:20, height:12, borderRadius:6, background:sel?'#2563eb':'#d1d5db', position:'relative', flexShrink:0 }}>
                          <div style={{ position:'absolute', top:2, left:sel?9:2, width:8, height:8, borderRadius:'50%', background:'white', transition:'left .1s' }} />
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:9, fontWeight:600, color:sel?'#1e40af':'#374151' }}>{p.name}</div>
                        </div>
                        {sel && p.qty_driver === 'manual' && (
                          <div onClick={e=>e.stopPropagation()} style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:0 }}>
                            <input type="number" min="0" value={qty || ''} placeholder="qty"
                              onChange={e=>{
                                const next = e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0);
                                setManualQuantities(prev => ({ ...prev, [p.id]: next }));
                              }}
                              style={{ width:48, padding:'1px 4px', border:`1px solid ${qty > 0 ? '#93c5fd' : '#fbbf24'}`, borderRadius:3, fontSize:9, fontFamily:'DM Mono, monospace', textAlign:'center', outline:'none', background: qty > 0 ? 'white' : '#fffbeb' }}/>
                            {qty === 0 && <span style={{ fontSize:7, color:'#92400e', fontWeight:600, marginTop:1 }}>set qty</span>}
                          </div>
                        )}
                        <div style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#6b7280' }}>${p.sell_price}/{p.qty_driver === 'manual' ? 'lic' : p.qty_driver}</div>
                      </div>
                    );
                  })}
                </div>
                );
              })}
            </Sec>

            {/* Locations */}
            <div style={{ marginBottom:10 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                <div>
                  <div style={{ fontSize:8, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase', color:'#374151' }}>
                    Locations <span style={{ color: locations.length >= 2 ? '#166534' : '#9ca3af' }}>({locations.length})</span>
                  </div>
                  {locations.length >= 2 && (
                    /* v3.5.95.0 — three display states for the margin-guarded
                       discount engine. Pre-deploy this always showed "X% multi-
                       location discount applied" where X was the unconditional
                       tier rate. Post-deploy: if the GM floor reduced the
                       rate, the rep sees the audit trail inline so they
                       understand WHY the discount got reduced (and so they
                       can choose to add cost, raise price, or accept the
                       smaller discount). If the rate is fully suppressed
                       (applied = 0), the row hides entirely — the red GM
                       indicator on the KPI strip carries the "this deal
                       can't meet floor" signal per Shaun's locked Q4. */
                    multiDiscRate > 0 ? (
                      multiDiscRateCapped ? (
                        <div style={{ fontSize:8, color:'#92400e', marginTop:1 }}>
                          {(multiDiscRateConfigured*100).toFixed(0)}% configured · {(multiDiscRate*100).toFixed(2).replace(/\.?0+$/, '')}% applied (GM floor)
                        </div>
                      ) : (
                        <div style={{ fontSize:8, color:'#166534', marginTop:1 }}>
                          {(multiDiscRate*100).toFixed(0)}% multi-location discount applied
                        </div>
                      )
                    ) : null
                  )}
                </div>
                <button onClick={openAddLoc}
                  style={{ padding:'4px 10px', background:'#0f1e3c', color:'white', border:'none', borderRadius:4, fontSize:10, fontWeight:600, cursor:'pointer' }}>
                  + Add Location
                </button>
              </div>

              {locations.map((l, i) => {
                const analysis = locationAnalyses[l.id];
                const res = locationResults.find(r => r.loc.id === l.id)?.result;
                return (
                  <div key={l.id} style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:6, padding:'8px 10px', marginBottom:6 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <div style={{ width:6, height:6, borderRadius:'50%', background: TYPE_COLOR[l.type], flexShrink:0 }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#0f1e3c', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.name}</div>
                        <div style={{ fontSize:9, color:'#6b7280' }}>
                          {[l.city, l.state].filter(Boolean).join(', ') || l.zip || 'No address'}
                          {' · '}{l.users} users · {l.workstations} WS
                          {analysis && <span style={{ color: TYPE_COLOR[analysis.market_tier === 'premium' ? 'standard' : 'restricted'], marginLeft:4 }}>· {analysis.market_tier}</span>}
                          {locLoading[l.id] && <span style={{ color:'#2563eb' }}> · analyzing...</span>}
                        </div>
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        {res && <div style={{ fontSize:11, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f1e3c' }}>{fmt$0(res.mrr)}/mo</div>}
                        <span style={{ fontSize:8, fontWeight:700, color: TYPE_COLOR[l.type], background: TYPE_BG[l.type], padding:'1px 5px', borderRadius:2 }}>
                          {LOCATION_TYPES[l.type]}
                        </span>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:4, marginTop:6 }}>
                      <button onClick={() => openEditLoc(l)}
                        style={{ flex:1, padding:'3px 0', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:3, fontSize:9, color:'#374151', cursor:'pointer' }}>Edit</button>
                      {locations.length > 1 && (
                        <button onClick={() => removeLoc(l.id)}
                          style={{ padding:'3px 8px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:3, fontSize:9, color:'#dc2626', cursor:'pointer' }}>✕</button>
                      )}
                    </div>
                  </div>
                );
              })}

              {locations.length >= 2 && (
                <button onClick={() => setShowConvertModal(true)}
                  style={{ width:'100%', padding:'5px', background:'white', border:'1px dashed #d1d5db', borderRadius:5, fontSize:9, color:'#6b7280', cursor:'pointer', marginTop:4 }}>
                  Convert to single-location IT quote
                </button>
              )}
            </div>

          </div>

          {/* Save */}
          <div style={{ borderTop:'1px solid #e5e7eb', padding:'10px 14px', background:'white', flexShrink:0 }}>
            <QuoteSaveBar
              onSave={saveQuote}
              existingQuote={existingQuote}
              quoteStatus={quoteStatus}
              setQuoteStatus={setQuoteStatus}
              quoteType="multisite"
              saving={saving}
              saveMsg={saveMsg}
              saveDisabled={!recipientBiz.trim()}
              saveLabelNew="Save Multi-Site Quote"
              saveButtonPadding="9px"
              saveButtonRadius={6}
              saveButtonFontSize={13}
              saveButtonFontWeight={700}
              showWrapper={false}
              extraButtons={
                <>
                  {/* v3.5.86.2: Customer Story & Assumptions entry point.
                      Mirrors QuotePage's v3.5.81.6 pattern — capture the
                      multi-location discovery context that AI grounds the
                      assumptions content against. Disabled until the quote
                      is saved (assumptions persist on `inputs.assumptions`,
                      so we need the FK first). The status indicator (✓
                      green vs ⚠ amber) tells the rep at a glance whether
                      the customer story has been captured for this quote. */}
                  <button
                    onClick={() => setShowAssumptions(true)}
                    disabled={!existingQuote?.id}
                    title={!existingQuote?.id ? 'Save the quote first' : (assumptionsComplete ? 'Customer Story captured — click to review or edit' : 'Capture multi-location discovery context')}
                    style={{
                      padding:'4px 8px',
                      background: assumptionsComplete ? '#f0fdf4' : '#fffbeb',
                      border: `1px solid ${assumptionsComplete ? '#bbf7d0' : '#fde68a'}`,
                      borderRadius:4, fontSize:10, fontWeight:600,
                      color: assumptionsComplete ? '#166534' : '#92400e',
                      cursor: !existingQuote?.id ? 'not-allowed' : 'pointer',
                      opacity: !existingQuote?.id ? 0.55 : 1,
                    }}>
                    {assumptionsComplete ? '✓' : '⚠'} Customer Story
                  </button>
                  <button onClick={exportSPT} style={{ padding:'4px 8px', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:4, fontSize:10, color:'#166534', fontWeight:600, cursor:'pointer' }}>
                    ↓ Export SPT
                  </button>
                </>
              }
            />
          </div>
        </div>

        {/* ── RIGHT: Preview ── */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'#f8fafc', minWidth:0 }}>
          <ReviewBanner quote={{ ...existingQuote, status: quoteStatus, hubspot_deal_id: hubDealId }} quoteType="multisite" onStatusChange={s => setQuoteStatus(s)} />

          {/* Price Lock Banner */}
          {pricingSnapshot && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 14px', background:'#1e3a5f', borderBottom:'1px solid #2d4f7a' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:14 }}>🔒</span>
                <div>
                  <span style={{ fontSize:11, fontWeight:700, color:'#93c5fd' }}>Prices locked</span>
                  <span style={{ fontSize:10, color:'#64748b', marginLeft:6 }}>as of {priceLockDate ? new Date(priceLockDate).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : 'approval'}</span>
                </div>
              </div>
              {(profile?.id === repId || profile?.role === 'admin') && (
                <button onClick={() => setShowUnlockModal(true)}
                  style={{ fontSize:10, padding:'3px 10px', background:'rgba(255,255,255,0.1)', color:'#93c5fd', border:'1px solid #2d4f7a', borderRadius:3, cursor:'pointer', fontWeight:600 }}>
                  Unlock Pricing
                </button>
              )}
            </div>
          )}
          {showUnlockModal && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 }}>
              <div style={{ background:'white', borderRadius:10, padding:28, width:440, boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
                <h3 style={{ fontSize:15, fontWeight:700, color:'#0f1e3c', margin:'0 0 10px' }}>⚠ Unlock Pricing</h3>
                <p style={{ fontSize:12, color:'#374151', lineHeight:1.6, margin:'0 0 14px' }}>Pricing was locked when this quote was approved. Unlocking allows rates to update from current package pricing.</p>
                <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:5, padding:'8px 12px', fontSize:11, color:'#991b1b', marginBottom:18 }}>
                  If this quote has been sent or exported to Smart Pricing Table, unlocking may cause price discrepancies. Consider a new revision instead.
                </div>
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button onClick={() => setShowUnlockModal(false)} style={{ padding:'7px 16px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:5, fontSize:12, cursor:'pointer' }}>Cancel</button>
                  <button onClick={async () => {
                    setPricingSnapshot(null); setPriceLockDate(null); setShowUnlockModal(false);
                    if (existingQuote?.id) await supabase.from('quotes').update({ pricing_snapshot: null, price_locked_at: null, price_locked_by: null }).eq('id', existingQuote.id);
                  }} style={{ padding:'7px 18px', background:'#dc2626', color:'white', border:'none', borderRadius:5, fontSize:12, fontWeight:700, cursor:'pointer' }}>Unlock & Recalculate</button>
                </div>
              </div>
            </div>
          )}
          <div style={{ flex:1, overflowY:'auto', padding:'14px 16px' }}>

            {/* Header */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <h2 style={{ fontSize:15, fontWeight:700, color:'#0f1e3c', margin:0 }}>{proposalName || recipientBiz || 'Multi-Site Quote Preview'}</h2>
                  <span style={{ fontSize:9, fontWeight:700, background:'#ede9fe', color:'#6d28d9', padding:'2px 7px', borderRadius:3 }}>MULTI-SITE</span>
                </div>
                <div style={{ fontSize:10, color:'#6b7280', marginTop:2 }}>
                  {locations.length} location{locations.length !== 1 ? 's' : ''} · {selectedPkg?.name} · {contractTerm}-month term
                </div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:8, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'.04em' }}>Total Monthly MRR</div>
                <div style={{ fontSize:22, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f1e3c' }}>{fmt$0(finalMRR)}</div>
              </div>
            </div>

            {/* KPI strip */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:7, marginBottom:10 }}>
              {[
                ['Locations', locations.length, '#6d28d9', '#faf5ff'],
                ['Location MRR', fmt$0(totalLocationMRR), '#0f1e3c', '#f0f4ff'],
                /* v3.5.95.0 — KPI strip cell: amber-tinted background when
                   the GM floor capped the rate, green when full rate applied,
                   em-dash when zero. Cell value compresses the audit trail
                   into "-3% (cap)" since the strip is tight; the pricing
                   card above and the line-items panel below carry the
                   full "X% configured · Y% applied" detail. */
                ['Multi-Site Disc',
                  multiDiscRate > 0 ? (multiDiscRateCapped ? `-${(multiDiscRate*100).toFixed(2).replace(/\.?0+$/, '')}% (cap)` : `-${(multiDiscRate*100).toFixed(0)}%`) : '—',
                  multiDiscRateCapped ? '#92400e' : '#166534',
                  multiDiscRateCapped ? '#fef3c7' : '#f0fdf4'],
                ['Total MRR', fmt$0(finalMRR), '#0f766e', '#f0fdfa'],
              ].map(([l, v, co, bg]) => (
                <div key={l} style={{ background:bg, borderRadius:5, padding:'7px 6px', textAlign:'center' }}>
                  <div style={{ fontSize:7, fontWeight:600, color:'#6b7280', letterSpacing:'.05em', textTransform:'uppercase', marginBottom:2 }}>{l}</div>
                  <div style={{ fontSize:13, fontWeight:700, fontFamily:'DM Mono, monospace', color:co }}>{v}</div>
                </div>
              ))}
            </div>

            {/* GM Target warning badge (v3.5.34) */}
            {(() => {
              const targetGM = parseFloat(settings?.target_gross_margin);
              const currentGM = effectiveMRR > 0 ? 1 - effectiveCost / effectiveMRR : 0;
              return (
                <div style={{ marginBottom: 10 }}>
                  <GMBadge currentGM={currentGM} targetGM={targetGM} locked={!!pricingSnapshot} />
                </div>
              );
            })()}

            {/* Per-location breakdown */}
            <div style={{ marginBottom:10 }}>
              <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6b7280', marginBottom:6 }}>Location Breakdown</div>
              {locationResults.map(({ loc: l, result: res, analysis }) => {
                const isExpanded = expandedLoc === l.id;
                return (
                  <div key={l.id} style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:6, marginBottom:6, overflow:'hidden' }}>
                    <div onClick={() => setExpandedLoc(isExpanded ? null : l.id)}
                      style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', cursor:'pointer', background: isExpanded ? '#f8fafc' : 'white' }}>
                      <div style={{ width:3, height:30, borderRadius:2, background: TYPE_COLOR[l.type], flexShrink:0 }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <span style={{ fontSize:11, fontWeight:700, color:'#0f1e3c' }}>{l.name}</span>
                          <span style={{ fontSize:8, fontWeight:700, color: TYPE_COLOR[l.type], background: TYPE_BG[l.type], padding:'1px 5px', borderRadius:2 }}>
                            {LOCATION_TYPES[l.type]}
                          </span>
                          {analysis && <span style={{ fontSize:8, color:'#6b7280' }}>{analysis.city}, {analysis.state}</span>}
                          {locLoading[l.id] && <span style={{ fontSize:8, color:'#2563eb' }}>analyzing...</span>}
                        </div>
                        <div style={{ fontSize:9, color:'#9ca3af', marginTop:1 }}>
                          {l.users} users · {l.workstations} WS{l.servers > 0 ? ` · ${l.servers} servers` : ''}
                          {analysis && ` · ${analysis.pricing_multiplier}× market`}
                        </div>
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        <div style={{ fontSize:12, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f1e3c' }}>{fmt$0(res?.mrr || 0)}/mo</div>
                        <div style={{ fontSize:8, color:'#9ca3af' }}>{isExpanded ? '▲' : '▼'}</div>
                      </div>
                    </div>

                    {isExpanded && res && (
                      <div style={{ padding:'0 12px 12px', borderTop:'1px solid #f1f5f9' }}>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:4, marginTop:8 }}>
                          {[
                            [`US (${l.users} × ${fmt$(selectedPkg?.user_rate)})`, res.breakdown.users],
                            [`WS (${l.workstations} × ${fmt$(selectedPkg?.ws_rate)})`, res.breakdown.workstations],
                            l.servers > 0 && [`Servers (${l.servers} × ${fmt$(selectedPkg?.server_rate)})`, res.breakdown.servers],
                            !res.isRestricted && res.breakdown.locationFee > 0 && [`Site Management Fee`, res.breakdown.locationFee],
                            // v3.5.61: Log Storage & SIEM Uplift with inline
                            // math (xEp dev × $rate — tier). epXEp/epRate/
                            // epTier come from calcLocationMRR breakdown.
                            res.breakdown.endpointUplift > 0 && [
                              `Log Storage & SIEM (${(res.breakdown.epXEp || 0) % 1 === 0 ? (res.breakdown.epXEp || 0).toFixed(0) : (res.breakdown.epXEp || 0).toFixed(1)} dev × $${res.breakdown.epRate} — ${res.breakdown.epTier})`,
                              res.breakdown.endpointUplift
                            ],
                          ].filter(Boolean).map(([label, val]) => (
                            <React.Fragment key={label}>
                              <div style={{ fontSize:9, color:'#6b7280' }}>{label}</div>
                              <div style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#374151', textAlign:'right' }}>{fmt$0(val)}/mo</div>
                            </React.Fragment>
                          ))}
                          {analysis?.pricing_multiplier && analysis.pricing_multiplier !== 1 && (
                            <>
                              <div style={{ fontSize:9, color:'#2563eb', gridColumn:'1/-1', paddingTop:4, borderTop:'1px solid #f1f5f9', marginTop:4 }}>
                                Market adjustment: {analysis.pricing_multiplier}× ({analysis.market_tier} — {analysis.city}, {analysis.state})
                              </div>
                            </>
                          )}
                          {res.isRestricted && (
                            <div style={{ fontSize:9, color:'#d97706', gridColumn:'1/-1' }}>⚠ Restricted — no network layer, no site fee</div>
                          )}
                        </div>

                        {/* On-site rates for this location */}
                        {analysis?.rates && (
                          <div style={{ background:'#f8fafc', borderRadius:4, padding:'6px 8px', marginTop:8 }}>
                            <div style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', color:'#9ca3af', marginBottom:4 }}>On-Site & OOS Rates — {l.name}</div>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:4 }}>
                              {[
                                ['Remote Support', analysis.rates.remote_support, '/hr'],
                                ['On-Site Dispatch', analysis.rates.onsite_block_2hr, ' block'],
                                ['On-Site Additional', analysis.rates.onsite_additional, '/hr'],
                              ].map(([label, rate, unit]) => (
                                <div key={label} style={{ textAlign:'center' }}>
                                  <div style={{ fontSize:8, color:'#9ca3af' }}>{label}</div>
                                  <div style={{ fontSize:11, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f1e3c' }}>{fmt$(rate)}{unit}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pricing summary */}
            <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:6, padding:12, marginBottom:10 }}>
              <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6b7280', marginBottom:8 }}>Pricing Summary</div>

              <LI lbl={`Location subtotal (${locations.length} locations)`} v={totalLocationMRR} bold />
              {/* v3.5.95.0 — line-items discount row reflects three states.
                  When capped, full audit trail in the label so the rep
                  has the answer to "why is the discount this much?" right
                  in the line item rather than having to cross-reference
                  the KPI strip or the pricing card. */}
              {multiDiscRate > 0 && (
                multiDiscRateCapped ? (
                  <LI lbl={`Multi-location discount (${(multiDiscRate*100).toFixed(2).replace(/\.?0+$/, '')}% applied · ${(multiDiscRateConfigured*100).toFixed(0)}% configured · capped by GM floor — ${locations.length} locations)`} v={-multiDiscAmount} ind />
                ) : (
                  <LI lbl={`Multi-location discount (${(multiDiscRate*100).toFixed(0)}% — ${locations.length} locations)`} v={-multiDiscAmount} ind />
                )
              )}
              {termDiscRate > 0 && <LI lbl={`Contract term discount (${(termDiscRate*100).toFixed(0)}% — ${contractTerm} mo)`} v={-termDisc} ind />}
              {/* v3.5.58: ITSO Advisory line at quote level — applied to total
                  base MRR across all locations, post-discount. Hidden when
                  Threat Profile = None. */}
              {itsoMRR > 0 && itsoLabel && (
                <>
                  <div style={{ margin:'5px 0', borderTop:'1px dashed #e5e7eb' }} />
                  <LI lbl={`${itsoLabel} (${(itsoUpliftRate * 100).toFixed(0)}% × labor base)`} v={itsoMRR} ind />
                </>
              )}
              {addonRevenue > 0 && (
                <>
                  <div style={{ margin:'5px 0', borderTop:'1px dashed #e5e7eb' }} />
                  <LI lbl="Add-on products" v={addonRevenue} bold />
                  {addonLineItems.map(li => (
                    <LI key={li.id} lbl={`${li.name} (${li.qty} × ${fmt$(li.sell_price)})`} v={li.revenue} ind muted />
                  ))}
                </>
              )}
              <div style={{ margin:'6px 0', borderTop:'2px solid #0f1e3c' }} />
              <LI lbl="✦ Total Monthly MRR" v={finalMRR} hi />

              {commissionRate > 0 && (
                <>
                  <div style={{ margin:'5px 0', borderTop:'1px dashed #e5e7eb' }} />
                  <LI lbl={`Commission — ${repProfile?.full_name || 'Rep'} (${fmtPct(commissionRate)} on ${fmt$0(commissionBase)})`} v={-commission} ind />
                  <LI lbl="Net after commission" v={netAfterCommission} bold />
                </>
              )}
            </div>

            {/* Cost Model panel (v3.5.39) — was previously absent on MultiSite */}
            <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:6, padding:12, marginBottom:10 }}>
              <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6b7280', marginBottom:8 }}>
                Cost Model — Monthly Recurring
              </div>

              {/* Tooling stack — per-tool breakdown across all locations (v3.5.39 / v3.5.41).
                  v3.5.57: legacy per-driver branch removed once every package
                  migrated to cost components — stack_cost_per_* settings deleted
                  in the same deploy, so this code path was unreachable. */}
              {totalToolingCost > 0 && (
                <>
                  <LI lbl="Tooling / stack" v={totalToolingCost} bold />

                  {/* Per-tool aggregation across locations: group by tool name + qty_driver
                      since the same tool could be configured for different drivers (rare but possible). */}
                  {(() => {
                    const agg = {};
                    for (const lr of locationResults) {
                      for (const t of (lr.result?.toolingTools || [])) {
                        const key = `${t.name}|${t.qty_driver}`;
                        if (!agg[key]) agg[key] = { name: t.name, qty_driver: t.qty_driver, qty: 0, rate: t.rate, total: 0 };
                        agg[key].qty += t.qty;
                        agg[key].total += t.total;
                      }
                    }
                    const tools = Object.values(agg).filter(t => t.total > 0 || t.qty > 0);
                    return tools.map((tool, i) => (
                      <LI key={i}
                        lbl={`  ${tool.name} (${tool.qty.toFixed(tool.qty < 10 ? 2 : 0)} × ${fmt$(tool.rate)} / ${tool.qty_driver})`}
                        v={tool.total} ind muted/>
                    ));
                  })()}

                  <div style={{ fontSize: 8, color: '#9ca3af', fontStyle: 'italic', marginTop: 1, marginBottom: 4, paddingLeft: 8 }}>
                    ↳ from {costComponents?.length || 0} cost components × {locations.length} location{locations.length === 1 ? '' : 's'}
                  </div>
                </>
              )}

              {/* Labor */}
              {totalSvcCost > 0 && (
                <LI lbl={`Labor (${totalSvcHrs.toFixed(1)} hrs × ${fmt$(burdenedRate)}/hr)`} v={totalSvcCost} ind />
              )}

              {/* Add-ons */}
              {addonCost > 0 && <LI lbl="Add-on delivery cost" v={addonCost} ind />}

              {/* Includes */}
              {totalIncludesCost > 0 && <LI lbl="Package includes (per-location)" v={totalIncludesCost} ind />}

              {/* Swap substitute cost */}
              {totalSwapCost > 0 && <LI lbl="Swap substitute delivery cost" v={totalSwapCost} ind />}

              {/* v3.5.58: ITSO Advisory senior labor cost — visible whenever
                  Threat Profile drives an uplift across all locations. */}
              {itsoMRR > 0 && itsoLabel && (
                <LI lbl={`${itsoLabel} labor (${itsoHours.toFixed(1)} hrs × ${fmt$(seniorBurden)}/hr cost rate)`} v={itsoCost} ind />
              )}

              {/* Flex labor */}
              {flexLaborCost > 0 && (
                <LI lbl={`Flex Time labor (${flexBlock?.hours} hrs × ${fmt$(burdenedRate)}/hr)`} v={flexLaborCost} ind />
              )}

              <div style={{ margin:'5px 0', borderTop:'1px solid #e5e7eb' }} />
              <LI lbl="Estimated Total Monthly Cost" v={effectiveCost} bold />

              {/* GM display with explicit footnote — v3.5.39 */}
              <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 6px', background:gb, borderRadius:4, marginTop:6 }}>
                <span style={{ fontSize:10, fontWeight:700, color:gc }}>Implied Gross Margin</span>
                <span style={{ fontSize:13, fontWeight:700, fontFamily:'DM Mono, monospace', color:gc }}>
                  {fmtPct(effectiveMRR > 0 ? 1 - effectiveCost / effectiveMRR : 0)}
                </span>
              </div>
              <div style={{ fontSize:8, color:'#9ca3af', marginTop:3, fontStyle:'italic' }}>
                Excludes onboarding and one-time fees — recurring revenue and recurring cost only
              </div>
            </div>

            {/* Onboarding + TCV */}
            {/* v3.5.86.5.1: Onboarding Fee and Contract TCV cards now reflect
                the onboarding incentive when one is applied — was showing the
                raw fee even after a 50% promo, so the on-page total disagreed
                with the Stripe prepay (which v3.5.86.4 fixed) and confused
                reps. Mirrors QuotePage line 2126/2128 pattern: display the
                effective (post-incentive) amount as the headline number, and
                surface a small "was $X · Y% off" caption when an incentive is
                active so the rep can see what the discount did. The
                computed `contractValue` and `onboarding` state vars are
                unchanged — display values are derived on the spot, keeping
                contract-math/persistence assumptions intact. */}
            {(() => {
              const effectiveOnboarding = obIncentive?.effectiveFee != null
                ? Number(obIncentive.effectiveFee)
                : onboarding;
              const incentiveActive = obIncentive?.effectiveFee != null
                && Number(obIncentive.effectiveFee) !== onboarding
                && onboarding > 0;
              const discountPctLabel = incentiveActive
                ? `${Math.round((1 - effectiveOnboarding / onboarding) * 100)}% off`
                : '';
              const effectiveTcv = finalMRR * contractTerm + effectiveOnboarding;
              return (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
                  <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:6, padding:'10px 12px', textAlign:'center' }}>
                    <div style={{ fontSize:8, fontWeight:700, color:'#0f766e', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:2 }}>One-Time · Onboarding Fee</div>
                    <div style={{ fontSize:16, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f766e' }}>{fmt$0(effectiveOnboarding)}</div>
                    {incentiveActive ? (
                      <div style={{ fontSize:8, color:'#92400e', marginTop:1, fontStyle:'italic' }}>
                        was {fmt$0(onboarding)} · {discountPctLabel}
                      </div>
                    ) : (
                      <div style={{ fontSize:8, color:'#9ca3af', marginTop:1 }}>not in GM</div>
                    )}
                  </div>
                  <div style={{ background:'#faf5ff', border:'1px solid #ddd6fe', borderRadius:6, padding:'10px 12px', textAlign:'center' }}>
                    <div style={{ fontSize:8, color:'#6b7280', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:2 }}>Contract TCV</div>
                    <div style={{ fontSize:16, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#6d28d9' }}>{fmt$0(effectiveTcv)}</div>
                    <div style={{ fontSize:8, color:'#9ca3af', marginTop:1 }}>{contractTerm} months</div>
                  </div>
                </div>
              );
            })()}

            {/* Covered locations — contractual assumption */}
            <div style={{ background:'#0f1e3c', borderRadius:6, padding:12, marginBottom:10 }}>
              <div style={{ fontSize:8, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', color:'#dc2626', marginBottom:6 }}>
                ⚖ Covered Locations — Contractual Scope
              </div>
              <div style={{ fontSize:9, color:'#94a3b8', marginBottom:8, lineHeight:1.5 }}>
                This agreement covers the following {locations.length} location{locations.length !== 1 ? 's' : ''} only.
                Any additional locations not listed are NOT covered and require a separate addendum.
              </div>
              {locations.map((l, i) => (
                <div key={l.id} style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:5, padding:'6px 8px', background:'rgba(255,255,255,0.05)', borderRadius:4, border:'1px solid rgba(255,255,255,0.08)' }}>
                  <span style={{ fontSize:9, color:'#475569', fontFamily:'DM Mono, monospace', flexShrink:0, marginTop:1 }}>{String(i+1).padStart(2,'0')}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#e2e8f0' }}>{l.name}</div>
                    <div style={{ fontSize:9, color:'#64748b' }}>
                      {[l.address, l.city, l.state, l.zip].filter(Boolean).join(', ') || 'Address not specified'}
                    </div>
                  </div>
                  <span style={{ fontSize:8, fontWeight:700, color: TYPE_COLOR[l.type], background: l.type === 'standard' ? '#1e3a5f' : '#451a03', padding:'2px 6px', borderRadius:2, flexShrink:0 }}>
                    {LOCATION_TYPES[l.type]}
                  </span>
                </div>
              ))}
            </div>

            {/* Payment notice */}
            <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:6, padding:'8px 11px', marginBottom:10 }}>
              <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#6b7280', marginBottom:4 }}>💳 Payment Methods</div>
              <div style={{ display:'flex', gap:16 }}>
                <div style={{ fontSize:10 }}><span style={{ color:'#374151' }}>ACH/EFT</span> <span style={{ fontWeight:700, color:'#166534' }}>Free</span></div>
                <div style={{ fontSize:10 }}><span style={{ color:'#374151' }}>Check</span> <span style={{ fontWeight:700, color:'#92400e' }}>+${checkFee} admin</span></div>
                <div style={{ fontSize:10 }}><span style={{ color:'#374151' }}>Credit Card</span> <span style={{ fontWeight:700, color:'#dc2626' }}>+{(ccSurcharge*100).toFixed(0)}% surcharge</span></div>
              </div>
            </div>

                        {/* Flex Time Meter */}
            <FlexTimeMeter
              pkg={selectedPkg}
              workstations={locations.reduce((s,l) => s + (parseInt(l.workstations)||0), 0)}
              users={locations.reduce((s,l) => s + (parseInt(l.users)||0), 0)}
              addonHours={null}
            />

{/* Onboarding Incentive */}
            {onboarding > 0 && (
              <OnboardingIncentive
                fee={onboarding}
                marketTier={locationResults.length > 0 && locationAnalyses[locations[0]?.id]
                  ? locationAnalyses[locations[0].id].market_tier
                  : 'standard'}
                contractTerm={contractTerm}
                initialValue={obIncentive}
                onChange={inc => {
                  setObIncentive(inc);
                  // v3.5.76: parity with QuotePage v3.5.73 — write a version
                  // snapshot on every incentive change so the activity log
                  // captures the rep's choice (including No-Incentive,
                  // where mode='none' post-v3.5.75).
                  if (existingQuote?.id) {
                    const label = inc?.mode === 'split'
                      ? `Onboarding: split over ${inc.splitMonths} months (+${fmt$0(inc.monthlyAdd)}/mo)`
                      : inc?.mode === 'discount'
                        ? (inc.discountPct === 100
                            ? `Onboarding: full waive applied — effective fee $0`
                            : `Onboarding: ${inc.discountPct}% discount applied — effective fee ${fmt$0(inc.effectiveFee)}`)
                        : 'Onboarding: no incentive selected — full fee applies';
                    saveQuoteVersion({ quoteId: existingQuote.id, quoteData: { client_name: recipientBiz, status: quoteStatus }, inputs: { proposalName, recipientContact, recipientEmail, recipientAddress, recipientPhone, recipientMobilePhone, hubspotContactId: hubContactId || null, hubspotCompanyId: hubCompanyId || null, hubspotSnapshot, notificationCc, addendums, package_name: selectedPkg?.name, locations }, totals: { finalMRR, onboarding: inc?.effectiveFee ?? onboarding }, lineItems: [], profile, note: label });
                  }
                }}
              />
            )}

            {/* v3.5.99.7.23: Manual discount lines (% or $/mo + note) */}
            <ManualDiscounts
              value={manualDiscounts}
              onChange={setManualDiscounts}
              baseMRR={finalMRRPreDiscount}
            />
            {manualDiscountResolved.total > 0 && (() => {
              const targetGM = parseFloat(settings?.target_gross_margin);
              const liveGM = effectiveMRR > 0 ? 1 - effectiveCost / effectiveMRR : 0;
              if (!isFinite(targetGM) || liveGM >= targetGM) return null;
              return (
                <div style={{ marginTop: 6, fontSize: 10, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '5px 8px' }}>
                  ⚠ Manual discount drops gross margin below target ({fmtPct(liveGM)} &lt; {fmtPct(targetGM)}). You can still send — review scope if this wasn&rsquo;t intended.
                </div>
              );
            })()}

            {/* v3.7.8: one-time (non-recurring) discounts — Onboarding bucket. */}
            <OneTimeDiscounts
              value={oneTimeDiscounts}
              onChange={setOneTimeDiscounts}
              bases={oneTimeBases}
            />

            {/* v3.5.99.7.24: optional back-dated agreement effective date */}
            <AgreementEffectiveDate value={effectiveDate} onChange={setEffectiveDate} />

            {/* Documents */}
            {/* v3.5.86.4: respect the onboarding incentive when computing
                Stripe's prepayment amount via DocumentsPanel's `prepayAmount`
                prop. Prior to this fix Multi-Site passed raw `onboarding`
                straight through, so Stripe charged the full pre-incentive
                fee even when an admin had applied a 50% promo. Mirrors
                QuotePage line 2623 / Bundle's prepay derivation at line
                1554-1561. obIncentive.effectiveFee is the post-incentive
                amount; falls back to raw `onboarding` when no incentive
                is active. */}
            <DocumentsPanel
              analysis={locationResults[0]?.analysis || null}
              settings={settings}
              clientName={recipientBiz}
              recipientContact={recipientContact}
              quoteId={existingQuote?.id}
              quoteNumber={existingQuote?.quote_number}
              sptProposalId={sptProposalId}
              onSPTLinked={(pid) => setSptProposalId(pid)}
              inputs={{ users: locations.reduce((s,l) => s+(parseInt(l.users)||0),0), workstations: locations.reduce((s,l) => s+(parseInt(l.workstations)||0),0), locations: locations.length, contractTerm, compliance, selectedProducts: [] }}
              pkg={selectedPkg}
              products={products}
              complianceKey={complianceKey}
              result={{ finalMRR, onboarding }}
              obIncentive={obIncentive}
              quoteType="multi-site-managed-it"
              clientEmail={recipientEmail}
              prepayAmount={oneTimeResolved.finalNRC}
              hubspotDealId={hubDealId}
              /* v3.5.86.3.2: page-driven Customer Story integration. See
                 QuotePage's mount for the full rationale — page owns the
                 AssumptionsModal mount, DocumentsPanel routes the click back
                 here. */
              onOpenAssumptions={() => setShowAssumptions(true)}
              onOpenAddendums={() => setAddendumsModalOpen(true)}
              addendumsCount={addendums.length}
              onOpenServiceOrderPreview={() => setSoPreviewModalOpen(true)}
              assumptionsSaved={assumptionsComplete}
            />

            {/* v3.5.99.6: client engagement panel — auto-hides on quotes with no proposal/SO activity yet */}
            <EngagementPanel quoteId={existingQuote?.id} />
            {/* v3.5.99.7.13: ETF Calculator — visible only on saved quotes
                (existingQuote?.id present means the quote was loaded/saved). */}
            {existingQuote?.id && (
              <button onClick={() => setShowEtfModal(true)}
                style={{ width:'100%', marginBottom:10, padding:'8px 12px', background:'white', border:'1px solid #e5e7eb', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600, color:'#0f1e3c', textAlign:'left', display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ fontSize:14 }}>💰</span>
                <span style={{ flex:1 }}>Calculate Early Termination Fee</span>
                <span style={{ fontSize:10, color:'#9ca3af', fontWeight:400 }}>→</span>
              </button>
            )}
            {showEtfModal && (
              <ETFCalculatorModal
                onClose={() => setShowEtfModal(false)}
                defaultMRR={finalMRR || ''}
                defaultTerm={contractTerm || 36}
                defaultClientName={recipientBiz}
                defaultClientAddress={recipientAddress}
                defaultClientContact={recipientContact}
                defaultClientEmail={recipientEmail}
                defaultContractReference={existingQuote?.quote_number || ''}
                quoteId={existingQuote?.id}
                hubDealId={hubDealId}
              />
            )}

            {/* Quote Notes */}
            <QuoteNotes
              quoteId={existingQuote?.id}
              quoteNumber={existingQuote?.quote_number}
              clientName={recipientBiz}
              hubDealId={hubDealId}
            />
            <QuoteHistory quoteId={existingQuote?.id} />

          </div>
        </div>
      </div>

      {/* v3.5.86.2: Customer Story & Assumptions modal mount.
          - quoteType="multi_site" routes the AI to the per-location prompt.
          - inputs.locations is what powers the HQ-location dropdown and
            the per-site detail block in the AI's user message.
          - inputs aggregates the engagement-level totals (sum of users
            across locations, etc.) so the AI also has a "size at a glance"
            number to anchor the recommendation against. */}
      {showAssumptions && (
        <AssumptionsModal
          quoteType="multi_site"
          onClose={() => setShowAssumptions(false)}
          quoteId={existingQuote?.id}
          quoteNumber={existingQuote?.quote_number}
          clientName={recipientBiz}
          recipientContact={recipientContact}
          inputs={{
            // Aggregated counts mirror what DocumentsPanel passes (lines 1444 area).
            users:        locations.reduce((s, l) => s + (parseInt(l.users)        || 0), 0),
            workstations: locations.reduce((s, l) => s + (parseInt(l.workstations) || 0), 0),
            servers:      locations.reduce((s, l) => s + (parseInt(l.servers)      || 0), 0),
            // Per-location detail — required by the multi_site capture UI
            // and the multi_site branch of buildAIPayload.
            locations,
            // Engagement-level config the AI uses for the recommendation.
            contractTerm, compliance, industryRisk, selectedProducts,
          }}
          pkg={selectedPkg}
          products={products}
          settings={settings}
          complianceKey={complianceKey}
          onSave={(a) => {
            setAssumptionsStory(a.story || '');
            setAssumptionsSavedAt(a.savedAt || null);
          }}
        />
      )}

      {/* v3.5.99.4: Special Terms (Addendums) modal */}
      <AddendumsModal
        open={addendumsModalOpen}
        onClose={() => setAddendumsModalOpen(false)}
        addendums={addendums}
        templates={addendumTemplates}
        onSave={setAddendums}
      />
      {/* v3.5.99.5: Service Order preview modal — controlled by the Preview button
          on the Documents card. docType is hardcoded per quote page since each
          page knows its own quote type. */}
      <ServiceOrderPreviewModal
        open={soPreviewModalOpen}
        onClose={() => setSoPreviewModalOpen(false)}
        quoteId={existingQuote?.id}
        docType="managed_it_quote"
        clientBiz={recipientBiz}
      />
    </>
  );
}

// ─── UI helpers — match QuotePage exactly ────────────────────────────────────
function Sec({t,c,children,s}){return(<div style={{marginBottom:10,...s}}><div style={{display:'flex',alignItems:'center',gap:4,marginBottom:5,paddingBottom:3,borderBottom:'1px solid #f1f5f9'}}><div style={{width:2,height:11,background:c||'#2563eb',borderRadius:2}}/><span style={{fontSize:9,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',color:'#6b7280'}}>{t}</span></div>{children}</div>);}
function Grid2({children}){return <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4}}>{children}</div>;}
function Fld({lbl,sub,children,s}){return(<div style={{marginBottom:4,...s}}><label style={{display:'block',fontSize:9,fontWeight:600,color:'#374151',marginBottom:1}}>{lbl}{sub&&<span style={{fontWeight:400,color:'#9ca3af',marginLeft:3,fontSize:9}}>{sub}</span>}</label>{children}</div>);}
function TI({value,onChange,placeholder}){return <input value={value||''} onChange={e=>onChange(e.target.value)} placeholder={placeholder||''} style={{width:'100%',padding:'4px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11,outline:'none'}}/>;}
function SI({v,s,opts}){return <select value={v} onChange={e=>s(e.target.value)} style={{width:'100%',padding:'4px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11,background:'white',outline:'none',color:'#374151'}}>{opts.map(([a,b])=><option key={a} value={a}>{b}</option>)}</select>;}
function LI({lbl,v,ind,bold,hi,muted}){if(v===0&&!bold&&!hi)return null;return(<div style={{display:'flex',justifyContent:'space-between',padding:hi?'5px 7px':'1px 2px',marginLeft:ind?7:0,borderRadius:hi?4:0,background:hi?'#dcfce7':'transparent',borderTop:bold&&!hi?'1px solid #f3f4f6':'none',marginTop:bold&&!hi?2:0}}><span style={{fontSize:hi?9:8,fontWeight:bold||hi?700:400,color:hi?'#166534':muted?'#9ca3af':bold?'#374151':'#6b7280'}}>{lbl}</span><span style={{fontSize:hi?11:9,fontWeight:bold||hi?700:500,fontFamily:'DM Mono, monospace',color:hi?'#166534':v<0?'#dc2626':bold?'#111827':'#374151'}}>{v<0?`(${fmt$(-v)})`:fmt$(v)}</span></div>);}
